import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Landmark, Percent, ShieldCheck, ArrowRight, Loader2, Calculator,
  FileCheck2, LogOut, Repeat, FileText, CreditCard, CheckCircle2,
  AlertTriangle, BellRing, UploadCloud, Pencil, Trash2, X, Home, Send,
  Plus, Car, Image as ImageIcon,
} from "lucide-react";
import BorrowerBinder from "./BorrowerBinder";
import SubmitToLender from "../components/SubmitToLender";
import TermsAndConditions from "../components/legal/TermsAndConditions";
import { SupportMessaging } from "../components/SupportMessaging";
import { InboxBell } from "../components/InboxBell";
import { US_STATES, instrumentForState, gaIntangibleTax, recommendedNoteStructure } from "../lib/securityInstrument";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import AccountDetailsPanel from "../components/AccountDetailsPanel";
import { CounterpartyAutocomplete } from "../components/CounterpartyAutocomplete";
import { SealWatermark } from "../components/SealWatermark";
const TKEY = "th_borrower_token";
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

const TX_TYPES = [
  { v: "refinance", label: "Refinance", icon: Percent, desc: "Replace your existing loan with a 0% note — no interest rolled in." },
  { v: "finance", label: "Asset Financing", icon: Landmark, desc: "Finance real estate or equipment." },
  { v: "debt_swap", label: "Swap", icon: Repeat, desc: "Resolve an obligation via instrument exchange (debt swap)." },
  { v: "debt_settlement", label: "Settlement", icon: Repeat, desc: "Settle an outstanding obligation with the counterparty." },
  { v: "consolidation", label: "Consolidation", icon: Landmark, desc: "Combine multiple debts/accounts into a single new note." },
  { v: "assumption", label: "Mortgage Assumption", icon: Home, desc: "Seutrade takes title & steps into your existing mortgage (subject-to) — you receive a cash-for-keys payment to move out." },
  { v: "foreclosure", label: "Foreclosure / Standstill", icon: ShieldCheck, desc: "Facing foreclosure? A 180-day standstill pauses lender enforcement while we refinance and pay off your existing loan." },
];

// Debts that share ONE consolidation closing (each is paid off & assigned).
const emptyDebt = () => ({ current_lender: "", current_lender_address: "", account_number: "", current_balance: "", debt_type: "mortgage" });

// One vehicle on an auto-refinance file. Each vehicle is billed a flat processing fee.
const _vid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : "v-" + Math.random().toString(36).slice(2));
const emptyVehicle = () => ({ id: _vid(), year: "", make: "", model: "", trim: "", vin: "", value: "", title_state: "", title_status: "clean", documents: [] });
// $850 per vehicle; every vehicle after the 4th is 50% off ($425).
const PER_VEHICLE_FEE = 850;
const autoFeeTotal = (n) => { const c = Math.max(1, Number(n) || 1); return Math.min(c, 4) * PER_VEHICLE_FEE + Math.max(0, c - 4) * (PER_VEHICLE_FEE / 2); };
const MORTGAGE_FEE = 1800;
const previewFee = (debtType, vehicleCount) => (debtType === "auto" ? autoFeeTotal(vehicleCount) : MORTGAGE_FEE);
const usd2 = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

// Transaction-type-correct summary line for the borrower's screen.
const txSummaryLine = (a) => {
  const t = a.transaction_type;
  const p = a.refi?.new_principal, pay = a.refi?.new_monthly_payment;
  const rate = Number(a.refi?.new_rate || 0), term = a.refi?.term_months, bal = a.current_balance;
  if (t === "assumption") return `Assumes existing ${fmt(p || bal)} note · ${fmt(pay)}/mo @ ${rate.toFixed(2)}% · cash-for-keys ${fmt(a.relocation_incentive || 500)}`;
  if (t === "consolidation") {
    const n = (a.additional_debts?.length || 0) + 1;
    const total = Number(bal || 0) + (a.additional_debts || []).reduce((s, d) => s + Number(d.current_balance || 0), 0);
    return `Consolidates ${n} account${n !== 1 ? "s" : ""} totaling ${fmt(total)} → new 0% note ${fmt(p)} · ${fmt(pay)}/mo over ${term} mo`;
  }
  if (t === "debt_swap") return `Instrument exchange · ${fmt(bal)} obligation resolved → 0% note ${fmt(p)} · ${fmt(pay)}/mo over ${term} mo`;
  if (t === "debt_settlement") return `Settlement of ${fmt(bal)} with ${a.current_lender || "the counterparty"} → 0% note ${fmt(p)} · ${fmt(pay)}/mo over ${term} mo`;
  if (t === "foreclosure") return `180-day standstill · payoff ${fmt(bal)} → 0% note ${fmt(p)} · ${fmt(pay)}/mo over ${term} mo`;
  const sav = a.refi?.monthly_savings ? ` (saves ${fmt(a.refi.monthly_savings)}/mo)` : "";
  return `Balance ${fmt(bal)} → New 0% note ${fmt(p)} · ${fmt(pay)}/mo over ${term} mo${sav}`;
};

// Mortgage lien-position chip label (consolidation/refinance/finance on a mortgage).
const lienChipLabel = (a) => {
  if (a.debt_type !== "mortgage") return null;
  if (!["consolidation", "refinance", "finance", "foreclosure"].includes(a.transaction_type)) return null;
  const pos = a.mortgage_position || "first";
  return `Mortgage · ${pos} lien position`;
};

export default function BorrowerPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || null);
  const [user, setUser] = useState(undefined);
  const [view, setView] = useState("home");

  const authAxios = useMemo(() => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} }), [token]);

  useEffect(() => {
    if (!token) { setUser(null); return; }
    authAxios.get("/borrower/me").then((r) => { setUser(r.data); setView("dashboard"); })
      .catch(() => { localStorage.removeItem(TKEY); setToken(null); setUser(null); });
  }, [token]); // eslint-disable-line

  const onAuthed = (data) => {
    localStorage.setItem(TKEY, data.token);
    setToken(data.token); setUser(data.user); setView("dashboard");
  };
  const logout = () => { localStorage.removeItem(TKEY); setToken(null); setUser(null); setView("home"); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4]" data-testid="borrower-portal">
      <SealWatermark fixed size={720} opacity={0.04} />
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#1C1C16] sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-20">
        <button onClick={() => setView(user ? "dashboard" : "home")} className="text-left">
          <div className="font-serif text-[20px] tracking-tight leading-none">SEUTRADE FINANCIAL SERVICES</div>
          <div className="text-[9.5px] tracking-[0.24em] uppercase text-[#8A8A8A] mt-1">Private Lender Services</div>
        </button>
        <div className="flex items-center gap-4">
          <a href="/" data-testid="borrower-home-link" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4]">← Home</a>
          {user ? (
            <>
              <span className="text-[12px] text-[#9A9A90]">{user.name}</span>
              <InboxBell client={authAxios} accent="#B8860B" />
              <button onClick={logout} data-testid="borrower-logout" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#B8860B] hover:text-[#d4a017]"><LogOut size={13} /> Sign out</button>
            </>
          ) : (
            <button onClick={() => setView("auth")} data-testid="borrower-signin-nav" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-[#B8860B] hover:text-[#d4a017]">Sign in / Apply <ArrowRight size={13} /></button>
          )}
        </div>
      </header>

      {view === "home" && <Marketing onStart={() => setView("auth")} />}
      {view === "auth" && <AuthPanel onAuthed={onAuthed} />}
      {view === "dashboard" && user && <Dashboard authAxios={authAxios} user={user} />}
      {user && user.must_change_password && (
        <ForcePasswordModal authAxios={authAxios} onDone={(u) => setUser((prev) => ({ ...prev, ...u, must_change_password: false }))} />
      )}
    </div>
  );
}

const ForcePasswordModal = ({ authAxios, onDone }) => {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await authAxios.put("/borrower/me", { new_password: pw });
      onDone(r.data.user || {});
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="force-password-modal">
      <div className="bg-[#13130E] border border-[#B8860B]/40 rounded-xl max-w-md w-full p-6">
        <h3 className="font-serif text-xl mb-1">Set your password</h3>
        <p className="text-[12px] text-[#9A9A90] mb-4">Your account was set up for you with a temporary password. For your security, please choose your own password to continue.</p>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8)" data-testid="force-pw-new"
          className="w-full bg-[#0F0F0F] border border-[#2A2A22] rounded-sm px-3 py-2.5 text-[14px] mb-3 focus:border-[#B8860B] focus:outline-none" />
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" data-testid="force-pw-confirm"
          className="w-full bg-[#0F0F0F] border border-[#2A2A22] rounded-sm px-3 py-2.5 text-[14px] mb-4 focus:border-[#B8860B] focus:outline-none" />
        <button onClick={save} disabled={busy} data-testid="force-pw-save"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] px-4 py-2.5 text-[12px] uppercase tracking-[0.16em] font-bold rounded-sm">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save &amp; Continue
        </button>
        {err && <div className="text-[11px] text-[#FF8585] mt-2" data-testid="force-pw-error">✗ {err}</div>}
      </div>
    </div>
  );
};

const Marketing = ({ onStart }) => (
  <main>
    <section className="px-6 py-20 max-w-5xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] mb-6"><ShieldCheck size={12} className="text-[#B8860B]" /> Private Capital · By Application</div>
      <h1 className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05]">Refinance to <span className="text-[#B8860B]">0% interest.</span><br />Keep more every month.</h1>
      <p className="text-[#9A9A90] text-base mt-6 max-w-2xl mx-auto leading-relaxed">SEUTRADE FINANCIAL SERVICES provides private financing, refinancing, and debt-settlement to qualified borrowers. Apply, e-sign your closing package, and set up automatic monthly payments — all in one portal.</p>
      <button onClick={onStart} data-testid="borrower-apply-cta" className="mt-9 inline-flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors">Apply for Financing <ArrowRight size={15} /></button>
    </section>
    <section className="px-6 pb-24 max-w-5xl mx-auto grid sm:grid-cols-3 gap-5">
      {TX_TYPES.map((t) => (
        <div key={t.v} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7">
          <div className="w-11 h-11 rounded-md bg-[#B8860B]/10 border border-[#B8860B]/30 flex items-center justify-center mb-5"><t.icon size={18} className="text-[#B8860B]" /></div>
          <div className="font-serif text-xl mb-2">{t.label}</div>
          <p className="text-[#8A8A8A] text-sm leading-relaxed">{t.desc}</p>
        </div>
      ))}
    </section>
  </main>
);

const AuthPanel = ({ onAuthed }) => {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const path = mode === "register" ? "/borrower/register" : "/borrower/login";
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const r = await axios.post(`${API}${path}`, body);
      onAuthed(r.data);
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };
  return (
    <main className="px-6 py-16 flex justify-center">
      <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-8 w-full max-w-md" data-testid="borrower-auth-panel">
        <div className="flex gap-2 mb-6">
          {["register", "login"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(null); }} data-testid={`borrower-tab-${m}`}
              className={`flex-1 py-2 text-[11px] uppercase tracking-[0.14em] font-bold rounded-sm ${mode === m ? "bg-[#B8860B] text-[#0A0A0A]" : "bg-[#0F0F0F] text-[#8A8A8A] border border-[#2A2A22]"}`}>
              {m === "register" ? "Create Account" : "Sign In"}
            </button>
          ))}
        </div>
        {mode === "register" && (
          <input placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="borrower-name" className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3" />
        )}
        <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          data-testid="borrower-email" className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3" />
        <input type="password" placeholder="Password (min 8 chars)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="borrower-password"
          className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-4" />
        <button onClick={submit} disabled={busy} data-testid="borrower-auth-submit"
          className="w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm flex items-center justify-center gap-2">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null}{mode === "register" ? "Create Account & Continue" : "Sign In"}
        </button>
        {mode === "login" && (
          <div className="text-center mt-3">
            <a href="/borrower/forgot-password" data-testid="borrower-forgot-link" className="text-[11px] text-[#8A8A8A] hover:text-[#E4C97A] transition-colors underline">Forgot password?</a>
          </div>
        )}
        {error && <div className="text-[11px] text-[#FF8585] mt-3" data-testid="borrower-auth-error">✗ {error}</div>}
      </div>
    </main>
  );
};

export const printPaymentReceipt = (r) => {
  const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const kindLabel = r.kind === "closing_fees" ? "Closing Costs" : (r.installment_n ? `Monthly Payment #${r.installment_n}` : "Payment");
  const row = (k, v) => (v ? `<tr><td class="k">${k}</td><td>${v}</td></tr>` : "");
  const rows = (r.items || []).map((it) => `<tr><td>${it.label}</td><td style="text-align:right">${money(it.amount)}</td></tr>`).join("");
  const w = window.open("", "_blank", "width=760,height=900");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${r.receipt_no}</title>
<style>@page{size:letter;margin:.7in}body{font-family:Georgia,serif;color:#141410;padding:24px 30px}
.wm{letter-spacing:.22em;font-weight:800;color:#0B4636;font-size:15px}.eb{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#8a6a10;font-weight:700;margin-top:2px}
h1{font-size:24px;margin:10px 0 4px}hr{border:none;border-top:2px solid #0B4636;margin:8px 0 16px}
table{width:100%;border-collapse:collapse;font-size:13px;margin:6px 0}td{border:1px solid #e2e2da;padding:7px 10px}.k{background:#f6f6f1;font-weight:600;width:42%}
.amt{text-align:right;font-size:17px;font-weight:800;margin-top:12px}.note{font-size:11px;color:#6c6c64;margin-top:18px;line-height:1.5}
.btn{margin:16px 0}@media print{.btn{display:none}}</style></head><body>
<div class="btn"><button onclick="window.print()">Print / Save PDF</button></div>
<div class="wm">SEUTRADE FINANCIAL SERVICES</div><div class="eb">Private Lender Services · Payment Receipt</div>
<h1>Payment Receipt</h1><hr/>
<table>${row("Receipt No.", r.receipt_no)}${row("Date", r.issued_at)}${row("Seutrade Account No.", r.seutrade_account_no)}${row("Payment For", kindLabel)}${row("Borrower", r.borrower_name)}${row("Email", r.borrower_email)}${row("Phone", r.borrower_phone)}${row("Address", r.borrower_address)}${row("Property / Collateral", r.property)}${row("Payment Method", r.payment_method)}<tr><td class="k">Reference</td><td style="font-family:monospace;font-size:11px">${String(r.ref || "").slice(0, 60)}</td></tr></table>
${rows ? `<table>${rows}</table>` : ""}
<div class="amt">Amount Paid: ${money(r.amount)} USD</div>
<p class="note">This receipt confirms funds received by Seutrade Financial Services for the item(s) above. Retain for your records. Official electronic receipt — no signature required.</p>
</body></html>`);
  w.document.close();
};

const BorrowerReceipts = ({ receipts }) => {
  if (!receipts || !receipts.length) return null;
  return (
    <section data-testid="borrower-receipts">
      <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><FileCheck2 size={17} className="text-[#3DBB8E]" /> Payment Receipts</h3>
      <div className="space-y-2">
        {receipts.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 bg-[#13130E] border border-[#2A2A22] rounded-lg px-5 py-3.5" data-testid={`receipt-${r.id}`}>
            <div>
              <div className="text-[13px] text-[#ECECE4] font-semibold">
                {r.kind === "closing_fees" ? "Closing Costs" : (r.installment_n ? `Monthly Payment #${r.installment_n}` : "Payment")} · {fmt(r.amount)}
              </div>
              <div className="text-[11px] text-[#8A8A8A] mt-0.5">
                {r.receipt_no} · {r.seutrade_account_no} · {r.issued_at}{r.emailed ? " · ✉ emailed" : ""}
              </div>
            </div>
            <button onClick={() => printPaymentReceipt(r)} data-testid={`receipt-view-${r.id}`}
              className="inline-flex items-center gap-1.5 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#E4C97A] px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
              <FileText size={13} /> View / Print
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

const Dashboard = ({ authAxios, user }) => {
  const [apps, setApps] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [binderApp, setBinderApp] = useState(null);
  const [signing, setSigning] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const reload = () => {
    authAxios.get("/borrower/applications").then((r) => setApps(r.data.applications)).catch(() => {});
    authAxios.get("/borrower/receipts").then((r) => setReceipts(r.data.receipts || [])).catch(() => {});
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line

  // Live refresh: pick up desk/admin edits without a manual reload.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") reload(); };
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", onVisible);
    const iv = setInterval(() => { if (document.visibilityState === "visible") reload(); }, 45000);
    return () => { window.removeEventListener("focus", reload); document.removeEventListener("visibilitychange", onVisible); clearInterval(iv); };
  }, []); // eslint-disable-line

  // Return from Stripe installment checkout → poll status
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sess = p.get("installment_session");
    if (!sess) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await authAxios.get(`/borrower/payments/status/${sess}`);
        if (r.data.payment_status === "paid") { await reload(); window.history.replaceState({}, "", "/borrower"); return; }
      } catch { /* ignore */ }
      if (++attempts < 6) setTimeout(poll, 2000);
      else window.history.replaceState({}, "", "/borrower");
    };
    poll();
  }, []); // eslint-disable-line

  // Return from Stripe card-setup (autopay) → confirm & save card on file
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sess = p.get("autopay_setup");
    const app = p.get("app");
    if (!sess || !app) return;
    (async () => {
      try { await authAxios.get(`/borrower/applications/${app}/autopay/status/${sess}`); await reload(); }
      catch { /* ignore */ }
      window.history.replaceState({}, "", "/borrower");
    })();
  }, []); // eslint-disable-line

  // Return from Stripe closing-costs payment
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sess = p.get("closing_fees");
    const app = p.get("app");
    if (!sess || !app) return;
    (async () => {
      try { await authAxios.get(`/borrower/applications/${app}/closing-fees/status/${sess}`); await reload(); }
      catch { /* ignore */ }
      window.history.replaceState({}, "", "/borrower");
    })();
  }, []); // eslint-disable-line


  const approved = apps.filter((a) => a.status === "approved");

  const openBinder = async (appId) => {
    try { const r = await authAxios.get(`/borrower/applications/${appId}`); setBinderApp(r.data.application); }
    catch { /* ignore */ }
  };
  const eSign = async () => {
    if (!binderApp) return;
    setSigning(true);
    try {
      await authAxios.post(`/borrower/applications/${binderApp.id}/sign`, {
        signer_name: binderApp.binder?.borrowerName || user.name,
        attestation: "I have read, understand, and agree to all terms of this closing package — including the Promissory Note; Disclosures, Arbitration & Borrower Covenants (binding arbitration, jury-trial & class-action waiver, hold-harmless); default/acceleration remedies; and automatic monthly payment authorization. I intend this as my legally binding electronic signature under the E-SIGN Act & UETA.",
      });
      const r = await authAxios.get(`/borrower/applications/${binderApp.id}`);
      setBinderApp(r.data.application); await reload();
    } catch (e) { alert(errMsg(e)); }
    setSigning(false);
  };
  const confirmEsign = async (appId) => {
    try {
      await authAxios.post(`/borrower/applications/${appId}/confirm-esign`, {});
      await reload();
    } catch (e) { alert(errMsg(e)); }
  };
  const toggleAutopay = async (appId, enroll) => {
    try { await authAxios.post(`/borrower/applications/${appId}/autopay`, { enroll, method: "card", day_of_month: 1 }); await reload(); }
    catch (e) { alert(errMsg(e)); }
  };
  const payInstallment = async (appId) => {
    setPayingId(appId);
    try {
      const r = await authAxios.post(`/borrower/applications/${appId}/pay-installment`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { alert(errMsg(e)); setPayingId(null); }
  };
  const setupCard = async (appId) => {
    setPayingId(appId);
    try {
      const r = await authAxios.post(`/borrower/applications/${appId}/autopay/setup`, { origin_url: window.location.origin, card_consent: true });
      window.location.href = r.data.url;
    } catch (e) { alert(errMsg(e)); setPayingId(null); }
  };
  const payClosing = async (appId, acceptTitle, acceptRefund) => {
    setPayingId(`cf_${appId}`);
    try {
      const r = await authAxios.post(`/borrower/applications/${appId}/pay-closing-fees`, { origin_url: window.location.origin, accept_title_insurance: acceptTitle, accept_refund_ncnd: !!acceptRefund });
      window.location.href = r.data.url;
    } catch (e) { alert(errMsg(e)); setPayingId(null); }
  };
  const refinanceBalloon = async (appId) => {
    if (!window.confirm("Refinance the balloon at maturity into a new short-term note? You will NOT pay the balloon in one lump sum — the outstanding balance rolls into a fresh 62-month note that remains GA intangible-tax exempt.")) return;
    setPayingId(appId);
    try {
      await authAxios.post(`/borrower/applications/${appId}/refinance-balloon`);
      await reload();
      alert("Balloon refinanced. A new short-term note has been added to your approved financing.");
    } catch (e) { alert(errMsg(e)); }
    setPayingId(null);
  };

  if (binderApp) {
    return <BorrowerBinder binder={binderApp.binder} signatures={binderApp.signatures || []}
      titleAccepted={binderApp.closing_fees?.title_insurance_accepted}
      borrowerView
      updatedAt={binderApp.last_edited_at || binderApp.updated_at || binderApp.created_at}
      onBack={() => setBinderApp(null)} onSign={eSign} signing={signing} />;
  }

  return (
    <main className="px-6 py-10 max-w-5xl mx-auto space-y-10" data-testid="borrower-dashboard">
      <h2 className="font-serif text-2xl">Welcome, {user.name.split(" ")[0]}</h2>

      <a href="/securities" data-testid="borrower-securities-tile"
        className="flex items-center gap-4 rounded-lg border border-[#B8860B]/40 bg-gradient-to-r from-[#1A1606] to-[#13130E] px-5 py-4 hover:border-[#E4C97A] transition-colors group">
        <span className="w-11 h-11 rounded-md bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center shrink-0"><Landmark size={20} className="text-[#C9A227]" /></span>
        <span className="flex-1">
          <span className="block text-[15px] font-serif text-[#E4C97A]">Open a SEUTRADE Securities Account</span>
          <span className="block text-[12px] text-[#9A9A90] mt-0.5">Letter of Credit · SBLC · Surety Bond · Proof of Funds — with a USD collateral ledger you can lend against. Fees just 0.5%.</span>
        </span>
        <ArrowRight size={16} className="text-[#C9A227] group-hover:translate-x-1 transition-transform" />
      </a>

      <a href="/tax-debt-program" data-testid="borrower-tax-program-tile"
        className="flex items-center gap-4 rounded-lg border border-[#14503B] bg-gradient-to-r from-[#06160F] to-[#0A241A] px-5 py-4 hover:border-[#6FD3AE] transition-colors group">
        <span className="w-11 h-11 rounded-md bg-[#6FD3AE]/10 border border-[#14503B] flex items-center justify-center shrink-0"><Landmark size={20} className="text-[#6FD3AE]" /></span>
        <span className="flex-1">
          <span className="block text-[15px] font-serif text-[#6FD3AE]">Tax Debt Assistance Program</span>
          <span className="block text-[12px] text-[#9A9A90] mt-0.5">Owe the IRS? Sign up to settle your tax debt with a transferred tax credit — and release liens, levies &amp; wage garnishments. Auto-built documents.</span>
        </span>
        <span data-testid="borrower-tax-program-signup" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-bold text-[#06160F] bg-[#6FD3AE] px-3.5 py-2 rounded-full">Sign Up <ArrowRight size={13} /></span>
      </a>

      <a href="/certificate?scope=member" data-testid="borrower-certificate-link"
        className="flex items-center gap-4 rounded-lg border border-[#8A6D1F]/50 bg-gradient-to-r from-[#17120a] to-[#1f1708] px-5 py-4 hover:border-[#E4C97A] transition-colors">
        <span className="w-11 h-11 rounded-md bg-[#B8860B]/10 border border-[#8A6D1F]/50 flex items-center justify-center shrink-0"><ShieldCheck size={20} className="text-[#E4C97A]" /></span>
        <span className="flex-1">
          <span className="block text-[15px] font-serif text-[#E4C97A]">Your Certificate of Authority</span>
          <span className="block text-[12px] text-[#9A9A90] mt-0.5">Your member certificate acknowledging membership in SEUTRADE (a § 508(c)(1)(A) faith-based organization) — printable &amp; eFaxable.</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-bold text-[#0B0B07] bg-[#E4C97A] px-3.5 py-2 rounded-full">View <ArrowRight size={13} /></span>
      </a>

      {approved.length > 0 && (
        <MyLoans loans={approved} onView={openBinder} onPay={payInstallment} onSetupCard={setupCard} onPayClosing={payClosing} payingId={payingId} onRefinance={refinanceBalloon} onConfirmEsign={confirmEsign} />
      )}

      {approved.length === 0 && apps.some((a) => a.status === "submitted") && (
        <PaymentReadyNotice />
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        <RefiCalculator />
        <ApplicationForm authAxios={authAxios} user={user} onSubmitted={reload} />
      </div>
      <MyApplications apps={apps} onView={openBinder} authAxios={authAxios} onChanged={reload} />
      <BorrowerReceipts receipts={receipts} />
      <BorrowerAccountSettings authAxios={authAxios} user={user} />
      <SupportMessaging client={authAxios} accent="#B8860B" />
    </main>
  );
};

const AcctInput = ({ label, val, on, type = "text", ph = "", testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">{label}</label>
    <input type={type} value={val} onChange={(e) => on(e.target.value)} placeholder={ph} data-testid={testid}
      className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
  </div>
);

const BorrowerAccountSettings = ({ authAxios, user }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const save = async () => {
    setSaving(true); setMsg(null);
    const body = { name: form.name, phone: form.phone, address: form.address };
    if (pw.new_password) { body.current_password = pw.current_password; body.new_password = pw.new_password; }
    try {
      await authAxios.put("/borrower/me", body);
      setMsg({ ok: true, t: "Account saved." });
      setPw({ current_password: "", new_password: "" });
      setOpen(false);
    } catch (e) { setMsg({ ok: false, t: e?.response?.data?.detail || "Could not save." }); }
    setSaving(false);
  };
  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6" data-testid="borrower-account-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-lg">My Account</h3>
          <p className="text-[12px] text-[#8A8A8A] mt-0.5">{user.name} · {user.email}</p>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="borrower-account-edit-toggle"
          className="text-[11px] uppercase tracking-[0.12em] font-bold text-[#E4C97A] border border-[#B8860B]/40 hover:border-[#B8860B] px-4 py-2 rounded-sm">{open ? "Cancel" : "Edit"}</button>
      </div>
      {open && (
        <div className="mt-5 space-y-3">
          <AcctInput label="Full Name" val={form.name} on={(v) => setForm((p) => ({ ...p, name: v }))} testid="borrower-account-name" />
          <AcctInput label="Phone" val={form.phone} on={(v) => setForm((p) => ({ ...p, phone: v }))} testid="borrower-account-phone" />
          <AcctInput label="Mailing Address" val={form.address} on={(v) => setForm((p) => ({ ...p, address: v }))} testid="borrower-account-address" />
          <div className="pt-3 border-t border-[#2A2A22]">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-2">Change Password (optional)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <AcctInput label="Current Password" type="password" val={pw.current_password} on={(v) => setPw((p) => ({ ...p, current_password: v }))} testid="borrower-account-curpw" />
              <AcctInput label="New Password (min 8)" type="password" val={pw.new_password} on={(v) => setPw((p) => ({ ...p, new_password: v }))} testid="borrower-account-newpw" />
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="borrower-account-save"
            className="w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm flex items-center justify-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save Changes
          </button>
        </div>
      )}
      {msg && <p className={`mt-3 text-[12px] ${msg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`} data-testid="borrower-account-msg">{msg.t}</p>}
    </div>
  );
};

const PaymentReadyNotice = () => (
  <section data-testid="payment-ready-notice"
    className="rounded-lg border border-[#B8860B]/40 bg-gradient-to-r from-[#1A1606] to-[#13130E] p-6">
    <div className="flex items-start gap-4">
      <span className="w-11 h-11 rounded-md bg-[#B8860B]/10 border border-[#B8860B]/30 flex items-center justify-center shrink-0">
        <CreditCard size={20} className="text-[#B8860B]" />
      </span>
      <div>
        <h3 className="font-serif text-lg text-[#E4C97A]">Paying your closing costs &amp; monthly payments</h3>
        <p className="text-[12.5px] text-[#C9C9C0] mt-1.5 leading-relaxed max-w-2xl">
          Once the desk <strong className="text-[#ECECE4]">approves your application</strong>, this area will show your
          approved financing with two secure options:
        </p>
        <ul className="text-[12px] text-[#9A9A90] mt-3 space-y-1.5">
          <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#3DBB8E] mt-0.5 shrink-0" /> <span><strong className="text-[#ECECE4]">Pay closing costs</strong> — one secure payment at electronic pre-closing.</span></li>
          <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#3DBB8E] mt-0.5 shrink-0" /> <span><strong className="text-[#ECECE4]">Add a debit/credit card on file</strong> — for automatic monthly note payments.</span></li>
        </ul>
        <p className="text-[10.5px] text-[#8A8A8A] mt-3 leading-relaxed max-w-2xl">
          Your card is entered on a bank-grade, PCI-secure page — card details are never stored on this site. You'll
          be prompted here as soon as your application is approved.
        </p>
      </div>
    </div>
  </section>
);

const printLoanStatement = (a) => {
  const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const acct = "STF-LN-" + String(a.id || "").replace(/-/g, "").slice(0, 10).toUpperCase();
  const acc = a.accounting || {};
  const sched = a.schedule || [];
  const principal = Number(a.refi?.new_principal || acc.amount_financed || a.current_balance || 0);
  let bal = principal;
  const rows = sched.map((s) => {
    const amt = Number(s.amount || 0);
    bal = Math.max(0, Number((bal - amt).toFixed(2)));
    const paid = s.status === "paid";
    return `<tr${paid ? ' style="background:#eef7f1"' : ""}><td>${s.n}</td><td>${s.due_date}${s.kind === "balloon" ? " (balloon)" : ""}</td><td style="text-align:right">${money(amt)}</td><td style="text-align:right">${money(amt)}</td><td style="text-align:right">${money(bal)}</td><td>${paid ? "✓ Paid" + (s.paid_at ? " " + String(s.paid_at).slice(0, 10) : "") : "Scheduled"}</td></tr>`;
  }).join("");
  const history = sched.filter((s) => s.status === "paid")
    .map((s) => `<tr><td>${s.paid_at ? String(s.paid_at).slice(0, 10) : "—"}</td><td>Payment #${s.n}</td><td style="text-align:right">${money(s.amount)}</td></tr>`).join("")
    || `<tr><td colspan="3" style="color:#6c6c64">No payments recorded yet.</td></tr>`;
  const w = window.open("", "_blank", "width=820,height=1000");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Loan Statement ${acct}</title>
<style>@page{size:letter;margin:.6in}body{font-family:Georgia,serif;color:#141410;padding:24px 30px}
.wm{letter-spacing:.22em;font-weight:800;color:#0B4636;font-size:16px}.eb{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#8a6a10;font-weight:700;margin-top:2px}
h1{font-size:23px;margin:12px 0 4px}h2{font-size:14px;margin:22px 0 6px;color:#0B4636;text-transform:uppercase;letter-spacing:.08em}
hr{border:none;border-top:2px solid #0B4636;margin:8px 0 14px}
table{width:100%;border-collapse:collapse;font-size:12px;margin:4px 0}td,th{border:1px solid #e2e2da;padding:6px 9px}th{background:#0B4636;color:#fff;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.06em}
.sum td{border:1px solid #e2e2da}.k{background:#f6f6f1;font-weight:600;width:32%}
.btn{margin:14px 0}@media print{.btn{display:none}}</style></head><body>
<div class="btn"><button onclick="window.print()">Print / Save PDF</button></div>
<div class="wm">SEUTRADE FINANCIAL SERVICES</div><div class="eb">Private Lender Services · Loan Statement</div>
<h1>Loan Statement</h1><hr/>
<table class="sum">
<tr><td class="k">Seutrade Account No.</td><td>${acct}</td><td class="k">Statement date</td><td>${new Date().toISOString().slice(0, 10)}</td></tr>
<tr><td class="k">Borrower</td><td>${a.borrower_name || ""}</td><td class="k">Loan type</td><td>0% Note · ${a.transaction_type || ""}</td></tr>
<tr><td class="k">Original principal</td><td>${money(principal)}</td><td class="k">Monthly payment</td><td>${money(a.refi?.new_monthly_payment)}</td></tr>
<tr><td class="k">Paid to date</td><td>${money(acc.paid_amount)}</td><td class="k">Current balance</td><td><b>${money(acc.remaining_balance)}</b></td></tr>
<tr><td class="k">Installments paid</td><td>${acc.paid_count}/${acc.total_installments}</td><td class="k">Next due</td><td>${acc.next_due ? money(acc.next_due.amount) + " on " + acc.next_due.due_date : "Paid in full"}</td></tr>
</table>
<h2>Payment History</h2>
<table><tr><th>Date</th><th>Description</th><th style="text-align:right">Amount</th></tr>${history}</table>
<h2>Amortization Schedule</h2>
<table><tr><th>#</th><th>Due date</th><th style="text-align:right">Payment</th><th style="text-align:right">Principal applied</th><th style="text-align:right">Balance after</th><th>Status</th></tr>${rows}</table>
<p style="font-size:10.5px;color:#6c6c64;margin-top:16px;line-height:1.5;">This statement is generated by Seutrade Financial Services for the account above and reflects the 0% note terms of record. Balances shown are principal-only (no interest accrues on a 0% note). For questions, contact Seutrade Financial Services Private Lender Services.</p>
</body></html>`);
  w.document.close();
};

const printPayoffLetter = (a) => {
  const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const acct = "STF-LN-" + String(a.id || "").replace(/-/g, "").slice(0, 10).toUpperCase();
  const acc = a.accounting || {};
  const sched = a.schedule || [];
  const principal = Number(a.refi?.new_principal || acc.amount_financed || a.current_balance || 0);
  const paidDates = sched.filter((s) => s.status === "paid" && s.paid_at).map((s) => String(s.paid_at).slice(0, 10)).sort();
  const lastPayment = a.paid_in_full_at ? String(a.paid_in_full_at).slice(0, 10) : (paidDates.length ? paidDates[paidDates.length - 1] : new Date().toISOString().slice(0, 10));
  const today = new Date().toISOString().slice(0, 10);
  const ref = "STF-SAT-" + String(a.id || "").replace(/-/g, "").slice(0, 8).toUpperCase();
  const w = window.open("", "_blank", "width=820,height=1000");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Loan Satisfaction Letter ${acct}</title>
<style>@page{size:letter;margin:.75in}body{font-family:Georgia,serif;color:#141410;padding:24px 30px;line-height:1.6}
.wm{letter-spacing:.22em;font-weight:800;color:#0B4636;font-size:17px}.eb{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#8a6a10;font-weight:700;margin-top:2px}
.firm{font-size:10.5px;color:#5c5c54;margin-top:2px}
h1{font-size:22px;margin:24px 0 4px;text-align:center;letter-spacing:.04em}
.stamp{display:inline-block;border:3px double #0B4636;color:#0B4636;font-weight:800;letter-spacing:.16em;padding:8px 20px;transform:rotate(-4deg);margin:10px 0;font-size:15px;text-transform:uppercase}
hr{border:none;border-top:2px solid #0B4636;margin:10px 0 18px}
table{width:100%;border-collapse:collapse;font-size:12px;margin:10px 0}td{border:1px solid #e2e2da;padding:7px 10px}.k{background:#f6f6f1;font-weight:600;width:34%}
.sig{margin-top:40px}.sigline{border-top:1px solid #141410;width:280px;margin-top:36px;padding-top:4px;font-size:11px}
.btn{margin:14px 0}@media print{.btn{display:none}}
.fine{font-size:10.5px;color:#6c6c64;margin-top:20px}</style></head><body>
<div class="btn"><button onclick="window.print()">Print / Save PDF</button></div>
<div class="wm">SEUTRADE FINANCIAL SERVICES</div><div class="eb">Private Lender Services</div>
<div class="firm">213 N Market Street #1011, Wilmington, DE 19801 · (877) 738-0943 · EIN 42-6951310</div>
<hr/>
<div style="text-align:center"><span class="stamp">Paid in Full · Satisfied</span></div>
<h1>Loan Satisfaction &amp; Payoff Letter</h1>
<p style="text-align:right;font-size:11.5px;color:#5c5c54">Date: ${today} · Ref: ${ref}</p>
<p><strong>${a.borrower_name || "Borrower"}</strong><br/>${a.property_address || a.collateral || ""}</p>
<p>Dear ${a.borrower_name || "Borrower"},</p>
<p>This letter confirms that the promissory note and loan identified below, held and serviced by <strong>Seutrade Financial Services</strong>, has been <strong>paid in full and satisfied</strong> as of <strong>${lastPayment}</strong>. No further payments are due, and any lien or security interest securing this note is hereby released.</p>
<table>
<tr><td class="k">Seutrade Account No.</td><td>${acct}</td></tr>
<tr><td class="k">Borrower</td><td>${a.borrower_name || ""}</td></tr>
<tr><td class="k">Loan type</td><td>0% Note · ${a.transaction_type || ""}</td></tr>
<tr><td class="k">Original principal</td><td>${money(principal)}</td></tr>
<tr><td class="k">Total paid</td><td>${money(acc.paid_amount || principal)}</td></tr>
<tr><td class="k">Remaining balance</td><td><strong>${money(0)}</strong></td></tr>
<tr><td class="k">Date satisfied</td><td>${lastPayment}</td></tr>
</table>
<p>We appreciate the opportunity to have served you. Please retain this letter for your records as evidence that this obligation has been fully satisfied and discharged.</p>
<div class="sig">
<p style="margin-bottom:0">Sincerely,</p>
<div class="sigline">John Avery, as Trustee<br/>SEUTRADE FINANCIAL SERVICES SPV Trust</div>
</div>
<p class="fine">This satisfaction letter is issued by Seutrade Financial Services for account ${acct}. It certifies the note referenced above is discharged in full. A 0% note accrues no interest; the balance shown is principal-only. Retain for your permanent records.</p>
</body></html>`);
  w.document.close();
};

const LoanStat = ({ label, value, tone }) => (
  <div className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3.5 py-2.5">
    <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#7A7A70] font-bold">{label}</div>
    <div className={`text-[15px] font-mono mt-0.5 ${tone === "good" ? "text-[#3DBB8E]" : tone === "gold" ? "text-[#E4C97A]" : "text-[#ECECE4]"}`}>{value}</div>
  </div>
);

const AmortizationSchedule = ({ a }) => {
  const [open, setOpen] = useState(false);
  const sched = a.schedule || [];
  const acc = a.accounting || {};
  const principal = Number(a.refi?.new_principal || acc.amount_financed || a.current_balance || 0);
  let bal = principal;
  const rows = sched.map((s) => {
    const amt = Number(s.amount || 0);
    const before = bal;
    bal = Math.max(0, Number((bal - amt).toFixed(2)));
    return { ...s, before, reduction: amt, after: bal };
  });
  const today = new Date().toISOString().slice(0, 10);
  if (!sched.length) return null;
  return (
    <div className="mt-4 border-t border-[#2A2A22] pt-4" data-testid={`amortization-${a.id}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`amortization-toggle-${a.id}`}
        className="w-full flex items-center justify-between gap-3 text-left group">
        <span className="text-[11px] uppercase tracking-[0.12em] text-[#B8860B] font-bold flex items-center gap-1.5">
          <FileText size={13} /> Amortization Schedule &amp; Balance
        </span>
        <span className="text-[10.5px] text-[#9A9A90] flex items-center gap-1.5">
          {acc.paid_count}/{acc.total_installments} paid · {fmt(acc.remaining_balance)} balance
          <span className="text-[#E4C97A] group-hover:underline">{open ? "Hide ▲" : "Show ▼"}</span>
        </span>
      </button>
      {open && (
        <div className="mt-3 overflow-x-auto rounded-md border border-[#2A2A22]">
          <table className="w-full min-w-[540px] text-[11.5px]" data-testid={`amortization-table-${a.id}`}>
            <thead className="bg-[#0A0A0A] text-[#7A7A70] uppercase tracking-[0.1em] text-[9.5px]">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Due date</th>
                <th className="px-3 py-2 text-right">Payment</th>
                <th className="px-3 py-2 text-right">Principal applied</th>
                <th className="px-3 py-2 text-right">Balance after</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isPaid = r.status === "paid";
                const overdue = !isPaid && r.due_date < today;
                return (
                  <tr key={r.n} className={`border-t border-[#1E1E18] ${isPaid ? "bg-[#0B6B4F]/8" : ""}`} data-testid={`amort-row-${a.id}-${r.n}`}>
                    <td className="px-3 py-2 text-[#8A8A8A]">{r.n}</td>
                    <td className="px-3 py-2 text-[#C9C9C0]">{r.due_date}{r.kind === "balloon" && <span className="ml-1 text-[9px] text-[#E4C97A] uppercase">balloon</span>}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#ECECE4]">{fmt(r.amount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#9A9A90]">{fmt(r.reduction)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#B8C4B8]">{fmt(r.after)}</td>
                    <td className="px-3 py-2">
                      {isPaid ? <span className="text-[#3DBB8E]">✓ Paid{r.paid_at ? ` ${String(r.paid_at).slice(0, 10)}` : ""}</span>
                        : overdue ? <span className="text-[#FF8585]">Past due</span>
                        : <span className="text-[#8A8A8A]">Scheduled</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MyLoans = ({ loans, onView, onPay, onSetupCard, onPayClosing, payingId, onRefinance, onConfirmEsign }) => {
  const [consent, setConsent] = useState({});
  return (
  <section data-testid="my-loans">
    <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><CheckCircle2 size={17} className="text-[#3DBB8E]" /> My Approved Financing</h3>
    <div className="space-y-4">
      {loans.map((a) => {
        const sched = a.schedule || [];
        const paid = sched.filter((s) => s.status === "paid").length;
        const nextDue = sched.find((s) => s.status !== "paid");
        const signed = (a.signatures || []).length > 0;
        const acc = a.accounting || {};
        const cf = a.closing_fees || {};
        const titleAccepted = cf.title_insurance_accepted || consent[`title_${a.id}`];
        const refundAccepted = !!consent[`refund_${a.id}`];
        const canPayFees = (!cf.title_applies || titleAccepted) && refundAccepted;
        const bns = (a.binder || {}).noteStructure || {};
        const btila = (a.binder || {}).tila || {};
        const isBalloon = Number(bns.balloonPayment || 0) > 0 && Number(bns.maturityMonths || 0) < Number(bns.amortizationMonths || 0);
        const balloonFinal = Number(btila.final_payment != null ? btila.final_payment : bns.balloonPayment || 0);
        const balloonMaturity = btila.maturity_date;
        const canRefiBalloon = isBalloon && (bns.refinanceOption || {}).enabled && !a.refinanced_to;
        return (
          <div key={a.id} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5" data-testid={`loan-${a.id}`}>
            {a.esign_pending_confirmation && (
              <div className="mb-4 flex items-start gap-2.5 bg-[#0B4A6B]/25 border border-[#3A8FB8]/60 rounded-md px-4 py-3" data-testid={`loan-confirm-esign-banner-${a.id}`}>
                <AlertTriangle size={16} className="text-[#7FC8E8] mt-0.5 shrink-0" />
                <div className="text-[12px] leading-relaxed text-[#CDE8F5]">
                  <strong className="text-[#7FC8E8]">Confirm your e-signature.</strong> Your broker e-signed your closing package on your behalf. Please review your binder, then confirm it is yours to make it final and unlock closing.{" "}
                  <button onClick={() => onView(a.id)} data-testid={`loan-confirm-esign-review-${a.id}`} className="underline underline-offset-2 font-semibold hover:text-white">Review binder</button>
                  <button onClick={() => onConfirmEsign(a.id)} data-testid={`loan-confirm-esign-btn-${a.id}`} className="ml-3 inline-flex items-center gap-1.5 bg-[#0B6B4F]/30 border border-[#0B6B4F]/70 text-[#3DBB8E] hover:bg-[#0B6B4F]/50 px-3 py-1 rounded-sm text-[11px] uppercase tracking-[0.08em] font-bold">Confirm my e-signature</button>
                </div>
              </div>
            )}
            {a.resign_required && (
              <div className="mb-4 flex items-start gap-2.5 bg-[#7A5B00]/25 border border-[#B8860B]/60 rounded-md px-4 py-3" data-testid={`loan-resign-banner-${a.id}`}>
                <AlertTriangle size={16} className="text-[#E4C97A] mt-0.5 shrink-0" />
                <div className="text-[12px] leading-relaxed text-[#F0DFA8]">
                  <strong className="text-[#E4C97A]">Documents updated — re-signature required.</strong> {a.resign_reason || "Your closing documents were updated. Please open your binder and e-sign the corrected package."}{" "}
                  <button onClick={() => onView(a.id)} data-testid={`loan-resign-open-${a.id}`} className="underline underline-offset-2 font-semibold hover:text-white">Review &amp; e-sign now →</button>
                </div>
              </div>
            )}
            {acc.status_label === "past_due" ? (
              <div className="mb-4 flex items-start gap-2.5 bg-[#9E2A2B]/15 border border-[#9E2A2B]/50 rounded-md px-4 py-3" data-testid={`loan-notice-pastdue-${a.id}`}>
                <AlertTriangle size={16} className="text-[#FF8585] mt-0.5 shrink-0" />
                <div className="text-[12px] leading-relaxed text-[#FFC9C9]">
                  <strong className="text-[#FF8585]">PAST DUE — {acc.past_due_count} installment{acc.past_due_count !== 1 ? "s" : ""} overdue ({fmt(acc.past_due_amount)}).</strong> Please pay immediately. Installments unpaid more than 90 days constitute an Event of Default under your covenants.
                </div>
              </div>
            ) : acc.status_label === "paid_in_full" ? (
              <div className="mb-4 flex items-center gap-2.5 bg-[#0B6B4F]/15 border border-[#0B6B4F]/50 rounded-md px-4 py-3" data-testid={`loan-notice-paid-${a.id}`}>
                <CheckCircle2 size={16} className="text-[#3DBB8E] shrink-0" />
                <div className="text-[12px] text-[#7DD3B8]"><strong>Paid in full.</strong> Your note is satisfied — no further payments are due.</div>
              </div>
            ) : acc.next_due ? (
              <div className="mb-4 flex items-center gap-2.5 bg-[#B8860B]/10 border border-[#B8860B]/40 rounded-md px-4 py-3" data-testid={`loan-notice-due-${a.id}`}>
                <BellRing size={15} className="text-[#B8860B] shrink-0" />
                <div className="text-[12px] text-[#E4C97A]"><strong>Amount due: {fmt(acc.next_due.amount)}</strong> on {acc.next_due.due_date} · {fmt(acc.remaining_balance)} balance remaining across {acc.remaining_count} payment{acc.remaining_count !== 1 ? "s" : ""}.</div>
              </div>
            ) : null}
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="font-semibold text-[15px]">{TX_TYPES.find((t) => t.v === a.transaction_type)?.label || a.transaction_type} · {a.current_lender}</div>
                {a.transaction_type === "assumption" && (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/60 bg-[#C8A24B]/12 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`loan-assumption-chip-${a.id}`}>
                    <Repeat size={11} /> Assumption · subject-to{a.mortgage_position && a.mortgage_position !== "first" ? ` · ${a.mortgage_position} lien` : ""}
                  </div>
                )}
                {lienChipLabel(a) && (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/50 bg-[#C8A24B]/10 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`loan-lien-chip-${a.id}`}>
                    <Home size={11} /> {lienChipLabel(a)}
                  </div>
                )}
                <div className="text-[12px] text-[#9A9A90] mt-1" data-testid={`loan-tx-summary-${a.id}`}>{txSummaryLine(a)}</div>
                <div className="text-[11.5px] text-[#8A8A8A] mt-1">{paid}/{sched.length} installments paid{nextDue ? ` · next due ${nextDue.due_date} (${fmt(nextDue.amount)})` : " · paid in full"}</div>
                <div className="text-[11px] mt-1">{signed ? <span className="text-[#3DBB8E]">✓ E-signed</span> : <span className="text-[#B8860B]">Awaiting your e-signature</span>}</div>
                {isBalloon && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/50 bg-[#C8A24B]/10 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`loan-balloon-chip-${a.id}`}>
                    <BellRing size={11} /> Balloon {fmt(balloonFinal)} due {balloonMaturity || "at maturity"} (mo {bns.maturityMonths})
                    {bns.gaShortTermExempt && <span className="text-[#3DBB8E]"> · GA tax-exempt</span>}
                  </div>
                )}
                {a.refinanced_to && <div className="mt-1 text-[10.5px] text-[#3DBB8E]" data-testid={`loan-balloon-refinanced-${a.id}`}>✓ Balloon refinanced into a successor short-term note</div>}
              </div>
              <div className="flex flex-col items-end gap-2">
                <button onClick={() => onView(a.id)} data-testid={`loan-view-binder-${a.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-3.5 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                  <FileText size={13} /> View / Sign / Print Binder
                </button>
                <button onClick={() => printLoanStatement(a)} data-testid={`loan-statement-${a.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#0B6B4F]/60 text-[#7DD3B8] hover:bg-[#0B6B4F]/15 px-3.5 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                  <FileText size={13} /> Download Statement
                </button>
                <button onClick={() => window.open(`/tax-credit/${a.id}`, "_blank")} data-testid={`loan-tax-credit-${a.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#0A241A] border border-[#14503B] text-[#6FD3AE] hover:bg-[#0B3D2E]/40 px-3.5 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                  <FileText size={13} /> Settle IRS Debt · Tax Credit
                </button>
                {acc.status_label === "paid_in_full" && (
                  <button onClick={() => printPayoffLetter(a)} data-testid={`loan-payoff-letter-${a.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#0B6B4F]/20 border border-[#0B6B4F]/70 text-[#3DBB8E] hover:bg-[#0B6B4F]/35 px-3.5 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                    <CheckCircle2 size={13} /> Payoff Letter
                  </button>
                )}
                {canRefiBalloon && (
                  <button onClick={() => onRefinance(a.id)} disabled={payingId === a.id} data-testid={`loan-refi-balloon-${a.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#C8A24B]/60 text-[#E4C97A] hover:bg-[#C8A24B]/15 px-3.5 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm disabled:opacity-50">
                    <BellRing size={13} /> Refinance Balloon
                  </button>
                )}
                <div className="flex items-center gap-2">
                  {a.autopay?.card_last4 ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-[#3DBB8E]" data-testid={`loan-card-onfile-${a.id}`}>
                      <CreditCard size={13} /> {String(a.autopay.card_brand || "card").toUpperCase()} ••••{a.autopay.card_last4}
                    </span>
                  ) : (
                    <button onClick={() => onSetupCard(a.id)} disabled={payingId === a.id || !consent[a.id]} data-testid={`loan-setup-card-${a.id}`}
                      title={!consent[a.id] ? "Check the authorization box below to enable" : ""}
                      className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#B8860B]/50 text-[#E4C97A] hover:bg-[#B8860B]/10 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                      {payingId === a.id ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={13} />} Add debit card
                    </button>
                  )}
                  {nextDue && (
                    <button onClick={() => onPay(a.id)} disabled={payingId === a.id} data-testid={`loan-pay-${a.id}`}
                      className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-3 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                      {payingId === a.id ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={13} />} Pay {fmt(nextDue.amount)}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3" data-testid={`loan-balance-summary-${a.id}`}>
              <LoanStat label="Amount financed" value={fmt(a.refi?.new_principal || acc.amount_financed || a.current_balance)} />
              <LoanStat label="Paid to date" value={fmt(acc.paid_amount)} tone="good" />
              <LoanStat label="Current balance" value={fmt(acc.remaining_balance)} tone="gold" />
            </div>

            {a.autopay?.card_last4 ? (
              <div className="text-[10.5px] text-[#7DD3B8] mt-3" data-testid={`loan-autopay-note-${a.id}`}>✓ Auto-debit active — {fmt(a.autopay.amount)}/mo will be charged to {String(a.autopay.card_brand || "card").toUpperCase()} ••••{a.autopay.card_last4} (exp {a.autopay.card_exp}) each month per your Note.</div>
            ) : (
              <div className="mt-4 border-t border-[#2A2A22] pt-4" data-testid={`payment-method-block-${a.id}`}>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#B8860B] font-bold mb-1.5 flex items-center gap-1.5"><CreditCard size={13} /> Payment method — add your debit / credit card</div>
                <p className="text-[11px] text-[#9A9A90] mb-2.5 leading-relaxed">Tick the authorization below to enable the <strong className="text-[#E4C97A]">"Add debit card"</strong> button above. Your card is entered on a secure page and used for your monthly note payments.</p>
                <label className="flex items-start gap-2.5 cursor-pointer" data-testid={`loan-card-consent-${a.id}`}>
                  <input type="checkbox" checked={!!consent[a.id]} onChange={(e) => setConsent((c) => ({ ...c, [a.id]: e.target.checked }))}
                    data-testid={`loan-card-consent-check-${a.id}`} className="mt-0.5 accent-[#B8860B] w-4 h-4 shrink-0" />
                  <span className="text-[10.5px] text-[#C9C9C0] leading-relaxed"><strong className="text-[#E4C97A]">I authorize and consent</strong> to Seutrade Financial Services charging my debit/credit card on file <strong>{fmt(a.refi?.new_monthly_payment)}/mo</strong> for each monthly note payment (and any past-due amount) automatically until my note is paid in full.</span>
                </label>
              </div>
            )}

            {/* Closing costs due at electronic pre-closing */}
            {!cf.paid ? (
              <div className="mt-4 border-t border-[#2A2A22] pt-4" data-testid={`closing-fees-${a.id}`}>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#B8860B] font-bold mb-2">Pre-Closing Costs — due at electronic pre-closing</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11.5px] text-[#C9C9C0] max-w-md">
                  {(cf.items || []).map((it) => (
                    <React.Fragment key={it.key}><span>{it.label}</span><span className="text-right font-mono">{fmt(it.amount)}</span></React.Fragment>
                  ))}
                  <span className="font-bold text-[#ECECE4] border-t border-[#2A2A22] pt-1 mt-1">Total due</span>
                  <span className="text-right font-mono font-bold text-[#E4C97A] border-t border-[#2A2A22] pt-1 mt-1">{fmt(cf.total)}</span>
                </div>
                {cf.title_applies && (
                  <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid={`title-accept-${a.id}`}>
                    <input type="checkbox" checked={!!titleAccepted} onChange={(e) => setConsent((c) => ({ ...c, [`title_${a.id}`]: e.target.checked }))}
                      data-testid={`title-accept-check-${a.id}`} className="mt-0.5 accent-[#B8860B] w-4 h-4 shrink-0" />
                    <span className="text-[10.5px] text-[#C9C9C0] leading-relaxed">I accept the <strong className="text-[#E4C97A]">Loan (Mortgagee) Title Insurance Policy</strong> (Amount of Insurance {fmt(cf.coverage_amount || a.current_balance)}) insuring marketable title and first-lien priority on the mortgaged property; the <strong>premium is already included in my {fmt(cf.total || MORTGAGE_FEE)} Pre-Closing Cost</strong> (no separate charge).</span>
                  </label>
                )}
                <label className="flex items-start gap-2 mt-3 max-w-md cursor-pointer" data-testid={`refund-accept-${a.id}`}>
                  <input type="checkbox" checked={refundAccepted} onChange={(e) => setConsent((c) => ({ ...c, [`refund_${a.id}`]: e.target.checked }))}
                    data-testid={`refund-accept-check-${a.id}`} className="mt-0.5 accent-[#B8860B] w-3.5 h-3.5 shrink-0" />
                  <span className="text-[9px] leading-[1.5] text-[#8A8A8A]">
                    I authorize this charge and agree to the Pre-Closing Cost of {fmt(cf.total || MORTGAGE_FEE)} for this transaction, governed by the <strong className="text-[#9A9A90]">Terms &amp; Conditions</strong> I accepted at application.
                  </span>
                </label>
                <button onClick={() => onPayClosing(a.id, !!titleAccepted, refundAccepted)} disabled={!canPayFees || payingId === `cf_${a.id}`} data-testid={`pay-closing-${a.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm"
                  title={!refundAccepted ? "Check the authorization box to enable" : ""}>
                  {payingId === `cf_${a.id}` ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={13} />} Pay pre-closing costs {fmt(cf.total)}
                </button>
              </div>
            ) : cf.waived ? (
              <div className="mt-3 text-[11px] text-[#5fd3a8]" data-testid={`closing-waived-${a.id}`}>★ Closing costs <strong>WAIVED — No Cost</strong>. No amounts are due on this account.</div>
            ) : (
              <div className="mt-3 text-[11px] text-[#3DBB8E]" data-testid={`closing-paid-${a.id}`}>✓ Closing costs paid ({fmt(cf.total)}) — {(cf.items || []).map((i) => i.label).join(", ")}.</div>
            )}

            <AmortizationSchedule a={a} />
          </div>
        );
      })}
    </div>
  </section>
  );
};

const RefiCalculator = () => {
  const [bal, setBal] = useState(300000);
  const [term, setTerm] = useState(360);
  const [pay, setPay] = useState(1896);
  const newPay = useMemo(() => (bal && term ? bal / term : 0), [bal, term]);
  const savings = useMemo(() => (pay ? pay - newPay : 0), [pay, newPay]);
  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7" data-testid="refi-calculator">
      <div className="flex items-center gap-2 mb-5"><Calculator size={16} className="text-[#B8860B]" /><span className="font-serif text-lg">0% Refinance Calculator</span></div>
      <Label>Current balance</Label>
      <NumInput value={bal} onChange={setBal} testid="calc-balance" />
      <Label>Current monthly payment</Label>
      <NumInput value={pay} onChange={setPay} testid="calc-payment" />
      <Label>New term (months)</Label>
      <NumInput value={term} onChange={setTerm} testid="calc-term" />
      <div className="mt-5 pt-5 border-t border-[#2A2A22] space-y-2 text-[13px]">
        <Row k="New principal (0% rate)" v={fmt(bal)} />
        <Row k="New monthly payment" v={fmt(newPay)} hi />
        <Row k="Estimated monthly savings" v={fmt(Math.max(0, savings))} good />
      </div>
      <p className="text-[10px] text-[#5C5C5C] mt-4 leading-snug">Illustrative. 0% refi carries only your current principal — no interest is rolled into the new note. Subject to application &amp; approval.</p>
    </div>
  );
};

const ApplicationForm = ({ authAxios, user, onSubmitted }) => {
  const [f, setF] = useState({
    transaction_type: "refinance", debt_type: "mortgage", property_or_collateral: "",
    vehicles: [emptyVehicle()],
    property_state: "", property_county: "", legal_description: "", tax_parcel_id: "", note_structure: "",
    mortgage_position: "first",
    current_lender: "", current_lender_address: "", account_number: "",
    counterparty_bank_name: "", counterparty_bank_aba: "", counterparty_bank_account: "",
    lender_fax: "", lender_email: "",
    current_balance: "", current_rate: "", current_payment: "", term_months: 360, notes: "",
    property_use: "residential", occupancy: "owner_occupied",
    assumption_existing_rate: "", assumption_monthly_payment: "", assumption_remaining_term: "",
    assumption_next_due_date: "", relocation_incentive: 500, vacate_date: "", deed_type: "warranty",
    borrower_full_name: user?.name || "", borrower_phone: "", borrower_address: "", borrower_ssn: "",
    borrower_dob: "", co_borrower_name: "", employer: "", annual_income: "",
    trust_name: "",
    broker_id: "",
    additional_debts: [],
  });
  const [brokers, setBrokers] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { authAxios.get("/borrower/brokers").then((r) => setBrokers(r.data.brokers || [])).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isConsolidation = f.transaction_type === "consolidation";
  const isAssumption = f.transaction_type === "assumption";
  const addDebt = () => setF((s) => ({ ...s, additional_debts: [...s.additional_debts, emptyDebt()] }));
  const rmDebt = (i) => setF((s) => ({ ...s, additional_debts: s.additional_debts.filter((_, x) => x !== i) }));
  const upDebt = (i, k, v) => setF((s) => ({ ...s, additional_debts: s.additional_debts.map((d, x) => x === i ? { ...d, [k]: v } : d) }));
  const addVehicle = () => setF((s) => ({ ...s, vehicles: [...(s.vehicles || []), emptyVehicle()] }));
  const rmVehicle = (i) => setF((s) => ({ ...s, vehicles: s.vehicles.length > 1 ? s.vehicles.filter((_, x) => x !== i) : s.vehicles }));
  const upVehicle = (i, k, v) => setF((s) => ({ ...s, vehicles: s.vehicles.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const vehCount = (f.vehicles || []).length;
  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      await authAxios.post("/borrower/applications", {
        ...f,
        terms_accepted: termsAccepted,
        current_balance: Number((f.debt_type === "auto" && !f.current_balance
          ? (f.vehicles || []).reduce((s, v) => s + Number(v.value || 0), 0)
          : f.current_balance) || 0),
        current_rate: f.current_rate ? Number(f.current_rate) : null,
        current_payment: f.current_payment ? Number(f.current_payment) : null,
        term_months: Number(f.term_months || 360),
        annual_income: f.annual_income ? Number(f.annual_income) : null,
        vehicles: f.debt_type === "auto"
          ? (f.vehicles || []).map((v) => ({ ...v, value: v.value ? Number(v.value) : null }))
          : [],
        assumption_existing_rate: isAssumption && f.assumption_existing_rate ? Number(f.assumption_existing_rate) : null,
        assumption_monthly_payment: isAssumption && f.assumption_monthly_payment ? Number(f.assumption_monthly_payment) : null,
        assumption_remaining_term: isAssumption && f.assumption_remaining_term ? Number(f.assumption_remaining_term) : null,
        relocation_incentive: isAssumption ? Number(f.relocation_incentive || 500) : null,
        additional_debts: isAssumption
          ? []
          : f.additional_debts.filter((d) => d.current_lender || d.current_balance)
              .map((d) => ({ ...d, current_balance: Number(d.current_balance || 0) })),
      });
      setMsg({ ok: true, t: "Application submitted, approved, and sent to your lender/servicer. Track the status below." });
      onSubmitted();
    } catch (e) { setMsg({ ok: false, t: errMsg(e) }); }
    setBusy(false);
  };
  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7" data-testid="application-form">
      <div className="flex items-center gap-2 mb-5"><FileCheck2 size={16} className="text-[#B8860B]" /><span className="font-serif text-lg">New Application</span></div>

      {brokers.length > 0 && (
        <div className="mb-4 rounded-md border border-[#2A2A22] bg-[#0F0F0A] p-4" data-testid="app-broker-section">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2">Working with a Broker? (optional)</div>
          <Label>Select your broker</Label>
          <select value={f.broker_id} onChange={(e) => up("broker_id", e.target.value)} data-testid="app-broker-select"
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px]">
            <option value="">— No broker / direct —</option>
            {brokers.map((b) => <option key={b.id} value={b.id}>{b.company}{b.contact_name ? ` · ${b.contact_name}` : ""}</option>)}
          </select>
          <p className="text-[11px] text-[#8A8A82] mt-2">If you select a broker, your application is filed directly into their portal so they can manage your transaction — you won't need to re-enter anything.</p>
        </div>
      )}

      <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2 mt-1">Your Details</div>
      <Label>Full legal name</Label>
      <Txt value={f.borrower_full_name} onChange={(v) => up("borrower_full_name", v)} testid="app-full-name" placeholder="e.g. John Q. Public" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Phone</Label><Txt value={f.borrower_phone} onChange={(v) => up("borrower_phone", v)} testid="app-phone" /></div>
        <div><Label>Date of birth</Label><Txt value={f.borrower_dob} onChange={(v) => up("borrower_dob", v)} testid="app-dob" type="date" /></div>
      </div>
      <Label>Mailing address</Label>
      <Txt value={f.borrower_address} onChange={(v) => up("borrower_address", v)} testid="app-address" placeholder="Street, City, State ZIP" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Social Security No.</Label><Txt value={f.borrower_ssn} onChange={(v) => up("borrower_ssn", v)} testid="app-ssn" placeholder="XXX-XX-XXXX" /></div>
        <div><Label>Co-borrower (optional)</Label><Txt value={f.co_borrower_name} onChange={(v) => up("co_borrower_name", v)} testid="app-coborrower" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Employer</Label><Txt value={f.employer} onChange={(v) => up("employer", v)} testid="app-employer" /></div>
        <div><Label>Annual income</Label><Txt value={f.annual_income} onChange={(v) => up("annual_income", v)} testid="app-income" type="number" /></div>
      </div>

      <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2 mt-4">Loan Details</div>
      <Label>Transaction type</Label>
      <select value={f.transaction_type} onChange={(e) => up("transaction_type", e.target.value)} data-testid="app-tx-type"
        className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
        {TX_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
      </select>
      <Label>Collateral type</Label>
      <select value={f.debt_type} onChange={(e) => up("debt_type", e.target.value)} data-testid="app-debt-type"
        className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
        <option value="mortgage">Mortgage / Real Estate</option>
        <option value="auto">Auto / Vehicle</option>
        <option value="student_loan">Student Loan</option>
        <option value="other">Other</option>
      </select>
      <Label>Property / collateral description</Label>
      <Txt value={f.property_or_collateral} onChange={(v) => up("property_or_collateral", v)} testid="app-collateral" placeholder={f.debt_type === "auto" ? "leave blank — we'll build this from the vehicle details below" : "e.g. 123 Main St, Memphis TN"} />
      <div className="mt-1 mb-2 rounded-md border border-[#2A2A22] bg-[#0F0F0A] px-3 py-2 text-[12px]" data-testid="app-fee-preview">
        <span className="text-[#8A8A82]">Pre-Closing Costs: </span>
        <span className="text-[#E4C97A] font-semibold">{usd2(previewFee(f.debt_type, vehCount))}</span>
        <span className="text-[#6f6f66]"> {f.debt_type === "auto" ? `($850 per vehicle × ${vehCount}${vehCount > 4 ? " · 50% off after the 4th" : ""})` : "(flat fee)"}</span>
      </div>
      {f.debt_type === "auto" && (
        <div className="mb-3 rounded-md border border-[#2A2A22] bg-[#0F0F0A] p-4" data-testid="app-vehicle-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold">Vehicles for Refinance</div>
            <span className="text-[10px] text-[#8A8A82]">$850 each · 50% off every vehicle after the 4th</span>
          </div>
          {(f.vehicles || []).map((v, i) => (
            <div key={v.id} className="mb-3 rounded-md border border-[#23231c] bg-[#0b0b07] p-3" data-testid={`app-vehicle-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold text-[#C9A227]">Vehicle {i + 1}{i >= 4 ? " · 50% discount" : ""} — {usd2(i >= 4 ? PER_VEHICLE_FEE / 2 : PER_VEHICLE_FEE)}</span>
                {(f.vehicles.length > 1) && (
                  <button type="button" onClick={() => rmVehicle(i)} data-testid={`app-vehicle-remove-${i}`}
                    className="text-[11px] text-[#C06A5A] hover:text-[#e5897a] inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><Label>Year</Label><Txt value={v.year} onChange={(val) => upVehicle(i, "year", val)} testid={`app-vehicle-year-${i}`} placeholder="2021" /></div>
                <div><Label>Make</Label><Txt value={v.make} onChange={(val) => upVehicle(i, "make", val)} testid={`app-vehicle-make-${i}`} placeholder="Toyota" /></div>
                <div><Label>Model</Label><Txt value={v.model} onChange={(val) => upVehicle(i, "model", val)} testid={`app-vehicle-model-${i}`} placeholder="Camry" /></div>
                <div><Label>Trim</Label><Txt value={v.trim} onChange={(val) => upVehicle(i, "trim", val)} testid={`app-vehicle-trim-${i}`} placeholder="SE" /></div>
              </div>
              <Label>VIN</Label>
              <Txt value={v.vin} onChange={(val) => upVehicle(i, "vin", val)} testid={`app-vehicle-vin-${i}`} placeholder="17-character VIN" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>Vehicle value (NADA/KBB)</Label><Txt value={v.value} onChange={(val) => upVehicle(i, "value", val)} testid={`app-vehicle-value-${i}`} type="number" /></div>
                <div><Label>Title status</Label>
                  <select value={v.title_status} onChange={(e) => upVehicle(i, "title_status", e.target.value)} data-testid={`app-vehicle-title-status-${i}`}
                    className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px]">
                    <option value="clean">Clean title</option>
                    <option value="lien">Title with existing lien</option>
                    <option value="salvage">Salvage</option>
                    <option value="rebuilt">Rebuilt</option>
                  </select>
                </div>
                <div><Label>Title state</Label>
                  <select value={v.title_state} onChange={(e) => upVehicle(i, "title_state", e.target.value)} data-testid={`app-vehicle-title-state-${i}`}
                    className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px]">
                    <option value="">Select state…</option>
                    {US_STATES.map(([ab, nm]) => <option key={ab} value={ab}>{nm}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={addVehicle} data-testid="app-vehicle-add"
            className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#B8860B] border border-[#8A6D1F] hover:border-[#E4C97A] px-3 py-1.5 rounded-sm">
            <Plus size={13} /> Add another vehicle
          </button>
          <p className="text-[11px] text-[#8A8A82] mt-2">Add every vehicle you want to refinance. You can upload each vehicle's title, registration, and photos from your file after submitting.</p>
        </div>
      )}
      <Label>Seutrade Complex Trust name (optional — you may name your trust)</Label>
      <Txt value={f.trust_name} onChange={(v) => up("trust_name", v)} testid="app-trust-name" placeholder="e.g. The Alexander Family Seutrade Complex Trust" />
      {f.debt_type === "mortgage" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Property state</Label>
            <select value={f.property_state} onChange={(e) => { up("property_state", e.target.value); up("note_structure", recommendedNoteStructure(e.target.value)); }} data-testid="app-property-state"
              className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-1">
              <option value="">Select state…</option>
              {US_STATES.map(([ab, nm]) => <option key={ab} value={ab}>{nm}</option>)}
            </select>
          </div>
          <div><Label>Property county</Label><Txt value={f.property_county} onChange={(v) => up("property_county", v)} testid="app-property-county" placeholder="e.g. Fulton" /></div>
        </div>
      )}
      {f.debt_type === "mortgage" && (
        <div className="mb-3">
          <Label>Mortgage lien position</Label>
          <select value={f.mortgage_position} onChange={(e) => up("mortgage_position", e.target.value)} data-testid="app-mortgage-position"
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px]">
            <option value="first">First mortgage (1st lien)</option>
            <option value="second">Second mortgage (2nd lien)</option>
            <option value="third">Third mortgage (3rd lien)</option>
          </select>
        </div>
      )}
      {isAssumption && (
        <div className="mb-3 rounded-md border border-[#B8860B]/40 bg-[#1A1606]/60 p-4" data-testid="app-assumption-fields">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#E4C97A] font-bold mb-1 flex items-center gap-1.5"><Home size={13} /> Mortgage Assumption (Subject-To)</div>
          <p className="text-[11px] text-[#9A9A90] mb-3 leading-relaxed">Seutrade takes title by deed and steps into your existing lender's note (same balance, rate &amp; payment). You receive a cash-for-keys payment to vacate. Enter your current note details:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Existing interest rate (%)</Label><Txt value={f.assumption_existing_rate} onChange={(v) => up("assumption_existing_rate", v)} testid="app-asm-rate" type="number" placeholder="6.5" /></div>
            <div><Label>Existing monthly payment ($)</Label><Txt value={f.assumption_monthly_payment} onChange={(v) => up("assumption_monthly_payment", v)} testid="app-asm-monthly" type="number" placeholder="1780.55" /></div>
            <div><Label>Remaining term (months)</Label><Txt value={f.assumption_remaining_term} onChange={(v) => up("assumption_remaining_term", v)} testid="app-asm-term" type="number" placeholder="276" /></div>
            <div><Label>Next payment due date</Label><Txt value={f.assumption_next_due_date} onChange={(v) => up("assumption_next_due_date", v)} testid="app-asm-nextdue" type="date" /></div>
            <div><Label>Cash-for-keys / move-out ($)</Label><Txt value={f.relocation_incentive} onChange={(v) => up("relocation_incentive", v)} testid="app-asm-incentive" type="number" placeholder="500" /></div>
            <div><Label>Vacate date</Label><Txt value={f.vacate_date} onChange={(v) => up("vacate_date", v)} testid="app-asm-vacate" type="date" /></div>
          </div>
          <Label>Deed transferring title to Seutrade</Label>
          <select value={f.deed_type} onChange={(e) => up("deed_type", e.target.value)} data-testid="app-asm-deed-type"
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px]">
            <option value="warranty">Warranty Deed</option>
            <option value="quitclaim">Quitclaim Deed</option>
          </select>
        </div>
      )}
      {f.debt_type === "mortgage" && instrumentForState(f.property_state) && (
        <div className="text-[11px] text-[#C9A227] mb-3 -mt-1" data-testid="app-security-instrument-hint">
          Security instrument for {f.property_state}: <strong>{instrumentForState(f.property_state)}</strong> will be generated for this filing.
        </div>
      )}
      {f.debt_type === "mortgage" && (
        <>
          <Label>Legal description (Exhibit A) — optional</Label>
          <textarea value={f.legal_description} onChange={(e) => up("legal_description", e.target.value)} data-testid="app-legal-description" rows={3}
            placeholder="Metes & bounds or lot/block legal description (recorders often require this beyond a street address)"
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3" />
          <Label>Tax Parcel / APN — optional</Label>
          <Txt value={f.tax_parcel_id} onChange={(v) => up("tax_parcel_id", v)} testid="app-tax-parcel" placeholder="e.g. R2001F-501" />
          <Label>Note structure</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2" data-testid="app-note-structure">
            {[
              { k: "standard", t: "Standard (fully amortizing)", d: `${f.term_months || 360}-month term` },
              { k: "balloon_62", t: "Short-term / 62-mo balloon", d: "GA intangible-tax exempt" },
            ].map((o) => (
              <button type="button" key={o.k} onClick={() => up("note_structure", o.k)} data-testid={`app-note-${o.k}`}
                className={`text-left px-3 py-2 rounded-sm border text-[12px] ${(f.note_structure || recommendedNoteStructure(f.property_state)) === o.k ? "border-[#C9A227] bg-[#1a1710]" : "border-[#3A3A3A]"}`}>
                <div className="font-semibold text-[#ECECE4]">{o.t}</div>
                <div className="text-[10px] text-[#8A8A8A]">{o.d}</div>
              </button>
            ))}
          </div>
          {f.property_state === "GA" && (
            <div className="text-[11px] mb-3" data-testid="app-ga-tax-hint">
              {(f.note_structure || recommendedNoteStructure(f.property_state)) === "balloon_62"
                ? <span className="text-[#3DBB8E]">✓ Exempt from Georgia intangible recording tax (short-term note ≤ 62 months, HB 586) — saves ~${gaIntangibleTax(f.current_balance).toLocaleString()}.</span>
                : <span className="text-[#E4C97A]">Standard term would incur ~${gaIntangibleTax(f.current_balance).toLocaleString()} GA intangible recording tax. Switch to the 62-mo balloon to avoid it.</span>}
            </div>
          )}
        </>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Property use</Label>
          <select value={f.property_use} onChange={(e) => up("property_use", e.target.value)} data-testid="app-property-use"
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
        </div>
        <div>
          <Label>Occupancy</Label>
          <select value={f.occupancy} onChange={(e) => up("occupancy", e.target.value)} data-testid="app-occupancy"
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
            <option value="owner_occupied">Owner-occupied</option>
            <option value="investment">Investment</option>
            <option value="second_home">Second home</option>
          </select>
        </div>
      </div>
      <Label>Current lender</Label>
      <div className="mb-2"><CounterpartyAutocomplete testid="app-lender-search" onSelect={(r) => { up("current_lender", r.name); if (r.address) up("current_lender_address", r.address); }} /></div>
      <Txt value={f.current_lender} onChange={(v) => up("current_lender", v)} testid="app-lender" placeholder="e.g. Wells Fargo Home Mortgage" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Lender address</Label><Txt value={f.current_lender_address} onChange={(v) => up("current_lender_address", v)} testid="app-lender-address" /></div>
        <div><Label>Account / loan no.</Label><Txt value={f.account_number} onChange={(v) => up("account_number", v)} testid="app-account" /></div>
      </div>
      <div className="mt-1 rounded-md border border-[#2A2A22] bg-[#0F0F0A] p-3" data-testid="app-counterparty-bank">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#8A8A80] font-bold mb-1">Counterparty bank (optional — enables a direct wire payoff)</div>
        <p className="text-[11px] text-[#9A9A90] mb-2 leading-relaxed">If you know your lender's <strong className="text-[#E4C97A]">bank name, ABA/routing and account</strong> for payoffs, add them so a purchasing bank can wire the payoff directly. Optional — a mailed certified check works without it.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label>Counterparty bank name</Label><Txt value={f.counterparty_bank_name} onChange={(v) => up("counterparty_bank_name", v)} testid="app-cp-bank-name" /></div>
          <div><Label>ABA / routing</Label><Txt value={f.counterparty_bank_aba} onChange={(v) => up("counterparty_bank_aba", v)} testid="app-cp-bank-aba" /></div>
          <div><Label>Account no.</Label><Txt value={f.counterparty_bank_account} onChange={(v) => up("counterparty_bank_account", v)} testid="app-cp-bank-account" /></div>
        </div>
      </div>
      <div className="mt-1 rounded-md border border-[#2A2A22] bg-[#0F0F0A] p-3" data-testid="app-lender-contact">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#B8860B] font-bold mb-2 flex items-center gap-1.5"><Send size={12} /> Lender / servicer delivery contact</div>
        <p className="text-[11px] text-[#9A9A90] mb-2 leading-relaxed">Provide your current lender/servicer's <strong className="text-[#E4C97A]">fax or email</strong> so we can transmit your approval package. Leave blank and we'll auto-look it up (FDIC / directory); if none is found, our team will print &amp; mail it.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Lender fax</Label><Txt value={f.lender_fax} onChange={(v) => up("lender_fax", v)} testid="app-lender-fax" placeholder="+13125551234" /></div>
          <div><Label>Lender email</Label><Txt value={f.lender_email} onChange={(v) => up("lender_email", v)} testid="app-lender-email" placeholder="payoffs@lender.com" /></div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Current balance</Label><Txt value={f.current_balance} onChange={(v) => up("current_balance", v)} testid="app-balance" type="number" /></div>
        <div><Label>Term (months)</Label><Txt value={f.term_months} onChange={(v) => up("term_months", v)} testid="app-term" type="number" /></div>
        <div><Label>Current rate %</Label><Txt value={f.current_rate} onChange={(v) => up("current_rate", v)} testid="app-rate" type="number" /></div>
        <div><Label>Current payment</Label><Txt value={f.current_payment} onChange={(v) => up("current_payment", v)} testid="app-curpay" type="number" /></div>
      </div>
      {!isAssumption && (
        <div className="mt-4 border border-[#3A3A2E] rounded-md p-4 bg-[#0F0F0B]" data-testid="app-additional-debts">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold">Additional accounts to consolidate</div>
            <button type="button" onClick={addDebt} data-testid="app-add-debt"
              className="inline-flex items-center gap-1 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#B8860B] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">+ Add account</button>
          </div>
          <p className="text-[11px] text-[#8A8A8A] mb-3">The account above is account #1. Add each additional debt to be paid off &amp; assigned in this one consolidation closing.</p>
          {f.additional_debts.length === 0 && <div className="text-[11px] text-[#6C6C64] italic">No extra accounts yet — click "Add account".</div>}
          {f.additional_debts.map((d, i) => (
            <div key={i} className="mb-3 pb-3 border-b border-[#2A2A22] last:border-0" data-testid={`app-debt-row-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-[#C9C9C0] font-semibold">Account #{i + 2}</span>
                <button type="button" onClick={() => rmDebt(i)} data-testid={`app-rm-debt-${i}`} className="text-[10px] text-[#FF8585] uppercase tracking-[0.1em]">Remove</button>
              </div>
              <div className="mb-2"><CounterpartyAutocomplete testid={`app-debt-search-${i}`} onSelect={(r) => { upDebt(i, "current_lender", r.name); if (r.address) upDebt(i, "current_lender_address", r.address); }} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Lender / servicer</Label><Txt value={d.current_lender} onChange={(v) => upDebt(i, "current_lender", v)} testid={`app-debt-lender-${i}`} /></div>
                <div><Label>Account / loan no.</Label><Txt value={d.account_number} onChange={(v) => upDebt(i, "account_number", v)} testid={`app-debt-account-${i}`} /></div>
                <div><Label>Balance</Label><Txt value={d.current_balance} onChange={(v) => upDebt(i, "current_balance", v)} testid={`app-debt-balance-${i}`} type="number" /></div>
                <div>
                  <Label>Collateral type</Label>
                  <select value={d.debt_type} onChange={(e) => upDebt(i, "debt_type", e.target.value)} data-testid={`app-debt-type-${i}`}
                    className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px]">
                    <option value="mortgage">Mortgage / Real Estate</option>
                    <option value="auto">Auto / Vehicle</option>
                    <option value="student_loan">Student Loan</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <Label>Lender address</Label>
              <Txt value={d.current_lender_address} onChange={(v) => upDebt(i, "current_lender_address", v)} testid={`app-debt-address-${i}`} />
            </div>
          ))}
          {f.additional_debts.length > 0 && (
            <div className="text-[11px] text-[#3DBB8E] mt-1" data-testid="app-consolidation-total">
              Total to consolidate: {fmt((Number(f.current_balance || 0)) + f.additional_debts.reduce((s, d) => s + Number(d.current_balance || 0), 0))}
            </div>
          )}
        </div>
      )}
      <TermsAndConditions accepted={termsAccepted} onAccept={setTermsAccepted} signerName={f.borrower_full_name} />
      <div className="mt-3 flex items-start gap-2 rounded-md border border-[#0B4636]/60 bg-[#0B4636]/10 px-3 py-2.5" data-testid="app-transmit-notice">
        <Send size={13} className="text-[#7DD3B8] mt-0.5 shrink-0" />
        <span className="text-[10.5px] leading-relaxed text-[#B8C4B8]">On submit, your application is approved and your <strong className="text-[#7DD3B8]">payoff request, authorization &amp; the applicable agreement</strong> are transmitted automatically to your current lender / servicer (by email or eFax; printed &amp; mailed if no contact is found).</span>
      </div>
      <button onClick={submit} disabled={busy || !f.current_lender || !f.current_balance || !f.borrower_full_name || !termsAccepted} data-testid="app-submit"
        className="w-full mt-4 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-40 text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm flex items-center justify-center gap-2">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}Submit Application &amp; Send to Lender
      </button>
      {msg && <div className={`text-[11px] mt-3 ${msg.ok ? "text-[#7DD3B8]" : "text-[#FF8585]"}`} data-testid="app-result">{msg.ok ? "✓" : "✗"} {msg.t}</div>}
    </div>
  );
};

const MyApplications = ({ apps, onView, authAxios, onChanged }) => {
  if (!apps.length) return null;
  return (
    <div data-testid="my-applications">
      <h3 className="font-serif text-lg mb-4">My Applications</h3>
      <div className="space-y-3">
        {apps.map((a) => <AppRow key={a.id} a={a} onView={onView} authAxios={authAxios} onChanged={onChanged} />)}
      </div>
    </div>
  );
};

const badgeCls = (s) => ({ submitted: "bg-[#B8860B]/15 text-[#B8860B] border-[#B8860B]/40", approved: "bg-[#0B6B4F]/20 text-[#3DBB8E] border-[#0B6B4F]/50", rejected: "bg-[#9E2A2B]/20 text-[#FF8585] border-[#9E2A2B]/40" }[s] || "");

const AppRow = ({ a, onView, authAxios, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const canEdit = a.status !== "rejected";
  const hasAccount = a.account_profile && Object.values(a.account_profile).some((v) => String(v || "").trim());
  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5" data-testid={`app-row-${a.id}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-semibold text-[14px]">{TX_TYPES.find((t) => t.v === a.transaction_type)?.label || a.transaction_type} · {a.current_lender}</div>
          {a.transaction_type === "assumption" && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/60 bg-[#C8A24B]/12 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`app-assumption-chip-${a.id}`}>
              <Repeat size={11} /> Assumption · subject-to{a.mortgage_position && a.mortgage_position !== "first" ? ` · ${a.mortgage_position} lien` : ""}
            </div>
          )}
          {lienChipLabel(a) && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/50 bg-[#C8A24B]/10 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`app-lien-chip-${a.id}`}>
              <Home size={11} /> {lienChipLabel(a)}
            </div>
          )}
          <div className="text-[11.5px] text-[#8A8A8A] mt-1" data-testid={`app-tx-summary-${a.id}`}>{txSummaryLine(a)}</div>
          {a.status === "approved" && <div className="text-[11px] text-[#3DBB8E] mt-1">✓ Approved — your financing package is ready above.</div>}
          {a.status === "rejected" && a.review_note && <div className="text-[11px] text-[#FF8585] mt-1">{a.review_note}</div>}
          {a.last_edited_by && <div className="text-[10.5px] text-[#6E6E64] mt-1" data-testid={`app-last-edited-${a.id}`}>Last edited by {a.last_edited_by}{a.last_edited_role ? ` (${a.last_edited_role})` : ""}{a.last_edited_at ? ` · ${new Date(a.last_edited_at).toLocaleDateString()}` : ""}{a.resign_required ? " · re-signature required" : ""}</div>}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button onClick={() => setEditing((e) => !e)} data-testid={`app-edit-toggle-${a.id}`}
              className="inline-flex items-center gap-1.5 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#E4C97A] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
              <Pencil size={12} /> {editing ? "Close" : "Edit"}
            </button>
          )}
          {a.status === "approved" && (
            <button onClick={() => onView(a.id)} data-testid={`app-view-${a.id}`}
              className="inline-flex items-center gap-1.5 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#B8860B] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm"><FileText size={12} /> Binder</button>
          )}
          <span className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] border rounded-sm ${badgeCls(a.status)}`}>{a.status}</span>
        </div>
      </div>

      {editing && canEdit && (
        <EditApplicationForm a={a} authAxios={authAxios}
          onSaved={() => { setEditing(false); onChanged && onChanged(); }} onCancel={() => setEditing(false)} />
      )}

      {a.debt_type === "auto" && (
        <VehiclesManager a={a} authAxios={authAxios} onChanged={onChanged} />
      )}

      <AppDocuments a={a} authAxios={authAxios} onChanged={onChanged} />

      <SubmitToLender app={a} authAxios={authAxios} base="borrower/applications" onChanged={onChanged} readOnly />

      {hasAccount && (
        <div className="mt-4">
          <button onClick={() => setShowAccount((v) => !v)} className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#E4C97A] hover:text-[#F0DFA8]" data-testid={`borrower-account-toggle-${a.id}`}>
            {showAccount ? "▾ Hide my account details" : "▸ View my account details"}
          </button>
          {showAccount && (
            <div className="mt-3">
              <AccountDetailsPanel application={a} editable={false} showNotes={false} theme="dark" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const VehiclesManager = ({ a, authAxios, onChanged }) => {
  const token = localStorage.getItem(TKEY);
  const persisted = React.useMemo(() => (a.vehicles || []).map((v) => ({ ...v })), [a.vehicles]);
  const [veh, setVeh] = useState(persisted.length ? persisted : [emptyVehicle()]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busyUp, setBusyUp] = useState(null);
  React.useEffect(() => { setVeh(persisted.length ? persisted : [emptyVehicle()]); }, [persisted]);
  const persistedIds = new Set(persisted.map((v) => v.id));
  const dirty = JSON.stringify(veh.map((v) => ({ ...v, documents: undefined }))) !== JSON.stringify(persisted.map((v) => ({ ...v, documents: undefined })));

  const upV = (i, k, val) => setVeh((s) => s.map((x, idx) => idx === i ? { ...x, [k]: val } : x));
  const addV = () => setVeh((s) => [...s, emptyVehicle()]);
  const rmV = (i) => setVeh((s) => s.length > 1 ? s.filter((_, x) => x !== i) : s);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await authAxios.put(`/borrower/applications/${a.id}/vehicles`,
        { vehicles: veh.map((v) => ({ ...v, value: v.value ? Number(v.value) : null })) });
      setMsg({ ok: true, t: "Vehicles saved — pre-closing cost updated." });
      onChanged && onChanged();
    } catch (e) { setMsg({ ok: false, t: errMsg(e) }); }
    setSaving(false);
  };
  const upload = async (vehicleId, kind, file) => {
    if (!file) return;
    setBusyUp(vehicleId + kind);
    const fd = new FormData(); fd.append("file", file); fd.append("doc_kind", kind);
    try {
      await authAxios.post(`/borrower/applications/${a.id}/vehicles/${vehicleId}/documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      onChanged && onChanged();
    } catch (e) { alert(errMsg(e)); }
    setBusyUp(null);
  };
  const delDoc = async (vehicleId, attId) => {
    if (!window.confirm("Remove this document?")) return;
    try { await authAxios.delete(`/borrower/applications/${a.id}/vehicles/${vehicleId}/documents/${attId}`); onChanged && onChanged(); }
    catch (e) { alert(errMsg(e)); }
  };

  const total = a.closing_fees?.total;
  return (
    <div className="mt-4 border-t border-[#2A2A22] pt-4" data-testid={`vehicles-manager-${a.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.12em] text-[#B8860B] font-bold flex items-center gap-1.5"><Car size={13} /> Vehicles ({veh.length}) — $850 each · 50% off after the 4th</div>
        {total != null && <div className="text-[11px] text-[#E4C97A] font-semibold" data-testid={`vehicles-total-${a.id}`}>Pre-Closing Cost: {usd2(total)}</div>}
      </div>
      {veh.map((v, i) => {
        const isPersisted = persistedIds.has(v.id);
        const docs = (persisted.find((p) => p.id === v.id)?.documents) || [];
        return (
          <div key={v.id} className="mb-3 rounded-md border border-[#23231c] bg-[#0b0b07] p-3" data-testid={`veh-${a.id}-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-[#C9A227]">Vehicle {i + 1}{i >= 4 ? " · 50% discount" : ""} — {usd2(i >= 4 ? 425 : 850)}</span>
              {veh.length > 1 && <button type="button" onClick={() => rmV(i)} data-testid={`veh-remove-${a.id}-${i}`} className="text-[11px] text-[#C06A5A] hover:text-[#e5897a] inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Year</Label><Txt value={v.year} onChange={(val) => upV(i, "year", val)} testid={`veh-year-${a.id}-${i}`} placeholder="2021" /></div>
              <div><Label>Make</Label><Txt value={v.make} onChange={(val) => upV(i, "make", val)} testid={`veh-make-${a.id}-${i}`} placeholder="Toyota" /></div>
              <div><Label>Model</Label><Txt value={v.model} onChange={(val) => upV(i, "model", val)} testid={`veh-model-${a.id}-${i}`} placeholder="Camry" /></div>
              <div><Label>Value</Label><Txt value={v.value} onChange={(val) => upV(i, "value", val)} testid={`veh-value-${a.id}-${i}`} type="number" /></div>
            </div>
            <Label>VIN</Label>
            <Txt value={v.vin} onChange={(val) => upV(i, "vin", val)} testid={`veh-vin-${a.id}-${i}`} placeholder="17-character VIN" />
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#8A8A82] font-bold mb-1">Title / registration / photos ({docs.length})</div>
              <div className="space-y-1 mb-2">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-[11px]" data-testid={`veh-doc-${d.id}`}>
                    <a href={`${API}/borrower/applications/${a.id}/vehicles/${v.id}/documents/${d.id}?auth=${token}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#7DD3B8] hover:text-[#3DBB8E]">
                      {(d.content_type || "").startsWith("image/") ? <ImageIcon size={12} /> : <FileText size={12} />} {d.filename} <span className="text-[#5C5C5C]">· {d.kind}</span>
                    </a>
                    <button onClick={() => delDoc(v.id, d.id)} data-testid={`veh-doc-del-${d.id}`} className="text-[#FF8585] hover:text-[#ff5252]" title="Remove"><Trash2 size={12} /></button>
                  </div>
                ))}
                {docs.length === 0 && <div className="text-[11px] text-[#5C5C5C]">No documents uploaded for this vehicle yet.</div>}
              </div>
              {isPersisted ? (
                <div className="flex items-center gap-2 flex-wrap">
                  {[["title", "Title"], ["registration", "Registration"], ["photo", "Photo"]].map(([kind, lbl]) => (
                    <label key={kind} data-testid={`veh-upload-${kind}-${a.id}-${i}`}
                      className="inline-flex items-center gap-1.5 bg-[#0F0F0F] border border-[#3A3A3A] hover:border-[#B8860B] cursor-pointer text-[10px] uppercase tracking-[0.08em] font-bold text-[#C9C9C0] px-2.5 py-1.5 rounded-sm">
                      {busyUp === v.id + kind ? <Loader2 size={11} className="animate-spin" /> : <UploadCloud size={11} />} {lbl}
                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" onChange={(e) => upload(v.id, kind, e.target.files?.[0])} />
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-[10.5px] text-[#9A8A5A]">Save vehicles to enable document uploads for this vehicle.</div>
              )}
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3">
        <button type="button" onClick={addV} data-testid={`veh-add-${a.id}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#B8860B] border border-[#8A6D1F] hover:border-[#E4C97A] px-3 py-1.5 rounded-sm"><Plus size={13} /> Add vehicle</button>
        <button type="button" onClick={save} disabled={saving || !dirty} data-testid={`veh-save-${a.id}`} className="inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-40 text-[#0A0A0A] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">{saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Save vehicles</button>
        {msg && <span className={`text-[11px] ${msg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{msg.t}</span>}
      </div>
    </div>
  );
};


const AppDocuments = ({ a, authAxios, onChanged }) => {
  const fileRef = React.useRef(null);
  const [docType, setDocType] = useState("government_id");
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem(TKEY);
  const docs = (a.documents || []).filter((d) => (d.source || "borrower") === "borrower");
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await authAxios.post(`/borrower/applications/${a.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged && onChanged();
    } catch (err) { alert(errMsg(err)); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const del = async (fileId) => {
    if (!window.confirm("Remove this document?")) return;
    try { await authAxios.delete(`/borrower/applications/${a.id}/documents/${fileId}`); onChanged && onChanged(); }
    catch (err) { alert(errMsg(err)); }
  };
  return (
    <div className="mt-4 border-t border-[#2A2A22] pt-4" data-testid={`app-documents-${a.id}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#B8860B] font-bold mb-2">Documents ({docs.length}) — upload or update your supporting files</div>
      <div className="space-y-1 mb-3">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-[11px]" data-testid={`app-doc-${d.id}`}>
            <a href={`${API}/borrower/documents/${d.id}?auth=${token}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-[#7DD3B8] hover:text-[#3DBB8E]">
              <FileText size={12} /> {d.original_filename} <span className="text-[#5C5C5C]">· {d.doc_type}</span>
            </a>
            {d.review_status && d.review_status !== "pending" && (
              <span className={`px-1.5 py-0.5 text-[8.5px] uppercase tracking-[0.08em] border rounded-sm ${d.review_status === "verified" ? "text-[#3DBB8E] border-[#0B6B4F]/50 bg-[#0B6B4F]/15" : "text-[#FF8585] border-[#9E2A2B]/50 bg-[#9E2A2B]/15"}`}>
                {d.review_status === "verified" ? "verified" : "needs attention"}
              </span>
            )}
            <button onClick={() => del(d.id)} data-testid={`app-doc-del-${d.id}`} className="text-[#FF8585] hover:text-[#ff5252]" title="Remove"><Trash2 size={12} /></button>
          </div>
        ))}
        {docs.length === 0 && <div className="text-[11px] text-[#5C5C5C]">No documents uploaded yet.</div>}
      </div>
      <div className="flex items-center gap-2">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid={`app-doctype-${a.id}`}
          className="bg-[#0F0F0F] border border-[#3A3A3A] rounded-sm text-[12px] px-2 py-1.5">
          <option value="government_id">Gov ID</option>
          <option value="income">Income</option>
          <option value="mortgage_statement">Mortgage stmt</option>
          <option value="property_deed">Deed/Title</option>
          <option value="other">Other</option>
        </select>
        <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid={`app-file-input-${a.id}`}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`app-upload-${a.id}`}
          className="inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Upload
        </button>
      </div>
    </div>
  );
};

const EditApplicationForm = ({ a, authAxios, onSaved, onCancel }) => {
  const [f, setF] = useState({
    transaction_type: a.transaction_type || "refinance", debt_type: a.debt_type || "mortgage",
    property_or_collateral: a.property_or_collateral || "", property_state: a.property_state || "",
    property_county: a.property_county || "", legal_description: a.legal_description || "",
    tax_parcel_id: a.tax_parcel_id || "",
    note_structure: a.note_structure || "", property_use: a.property_use || "residential",
    occupancy: a.occupancy || "owner_occupied",
    current_lender: a.current_lender || "", current_lender_address: a.current_lender_address || "",
    account_number: a.account_number || "", current_balance: a.current_balance ?? "",
    counterparty_bank_name: a.counterparty_bank_name || "", counterparty_bank_aba: a.counterparty_bank_aba || "",
    counterparty_bank_account: a.counterparty_bank_account || "",
    current_rate: a.current_rate ?? "", current_payment: a.current_payment ?? "",
    term_months: a.term_months || 360,
    borrower_full_name: a.borrower_full_name || a.borrower_name || "", borrower_phone: a.borrower_phone || "",
    borrower_address: a.borrower_address || "", borrower_ssn: "", borrower_dob: a.borrower_dob || "",
    co_borrower_name: a.co_borrower_name || "", employer: a.employer || "", annual_income: a.annual_income ?? "",
    mortgage_position: a.mortgage_position || "first",
    assumption_existing_rate: a.assumption_existing_rate ?? "", assumption_monthly_payment: a.assumption_monthly_payment ?? "",
    assumption_remaining_term: a.assumption_remaining_term ?? "", assumption_next_due_date: a.assumption_next_due_date || "",
    relocation_incentive: a.relocation_incentive ?? 500, vacate_date: a.vacate_date || "", deed_type: a.deed_type || "warranty",
    trust_name: a.trust_name || "",
    additional_debts: Array.isArray(a.additional_debts) ? a.additional_debts.map((d) => ({ ...d })) : [],
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isAsm = f.transaction_type === "assumption";
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await authAxios.put(`/borrower/applications/${a.id}`, {
        ...f,
        current_balance: Number(f.current_balance || 0),
        current_rate: f.current_rate ? Number(f.current_rate) : null,
        current_payment: f.current_payment ? Number(f.current_payment) : null,
        term_months: Number(f.term_months || 360),
        annual_income: f.annual_income ? Number(f.annual_income) : null,
        mortgage_position: f.mortgage_position || null,
        assumption_existing_rate: isAsm && f.assumption_existing_rate ? Number(f.assumption_existing_rate) : null,
        assumption_monthly_payment: isAsm && f.assumption_monthly_payment ? Number(f.assumption_monthly_payment) : null,
        assumption_remaining_term: isAsm && f.assumption_remaining_term ? Number(f.assumption_remaining_term) : null,
        assumption_next_due_date: isAsm ? (f.assumption_next_due_date || null) : null,
        relocation_incentive: isAsm ? Number(f.relocation_incentive || 500) : null,
        vacate_date: isAsm ? (f.vacate_date || null) : null,
        deed_type: isAsm ? (f.deed_type || "warranty") : null,
        trust_name: f.trust_name || "",
        additional_debts: isAsm ? [] : (f.additional_debts || []).filter((d) => d.current_lender || d.current_balance).map((d) => ({ ...d, current_balance: Number(d.current_balance || 0) })),
      });
      onSaved && onSaved();
    } catch (e) { setMsg(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="mt-4 border-t border-[#2A2A22] pt-4" data-testid={`app-edit-form-${a.id}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2">Edit Application — changes rebuild your binder &amp; schedule (you'll need to re-sign if already signed)</div>
      <Label>Full legal name</Label>
      <Txt value={f.borrower_full_name} onChange={(v) => up("borrower_full_name", v)} testid={`edit-name-${a.id}`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Phone</Label><Txt value={f.borrower_phone} onChange={(v) => up("borrower_phone", v)} testid={`edit-phone-${a.id}`} /></div>
        <div><Label>Date of birth</Label><Txt value={f.borrower_dob} onChange={(v) => up("borrower_dob", v)} testid={`edit-dob-${a.id}`} type="date" /></div>
      </div>
      <Label>Mailing address</Label>
      <Txt value={f.borrower_address} onChange={(v) => up("borrower_address", v)} testid={`edit-address-${a.id}`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Employer</Label><Txt value={f.employer} onChange={(v) => up("employer", v)} testid={`edit-employer-${a.id}`} /></div>
        <div><Label>Annual income</Label><Txt value={f.annual_income} onChange={(v) => up("annual_income", v)} testid={`edit-income-${a.id}`} type="number" /></div>
      </div>
      <Label>Transaction type</Label>
      <select value={f.transaction_type} onChange={(e) => up("transaction_type", e.target.value)} data-testid={`edit-tx-type-${a.id}`}
        className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
        {TX_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
      </select>
      {f.debt_type === "mortgage" && (
        <>
          <Label>Mortgage lien position</Label>
          <select value={f.mortgage_position} onChange={(e) => up("mortgage_position", e.target.value)} data-testid={`edit-mortgage-position-${a.id}`}
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
            <option value="first">First (1st)</option>
            <option value="second">Second (2nd)</option>
            <option value="third">Third (3rd)</option>
          </select>
        </>
      )}
      {isAsm && (
        <div className="mb-3 rounded-md border border-[#B8860B]/40 bg-[#1A1606]/60 p-4" data-testid={`edit-assumption-fields-${a.id}`}>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2">Mortgage Assumption (subject-to) — existing note terms</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Existing interest rate (%)</Label><Txt value={f.assumption_existing_rate} onChange={(v) => up("assumption_existing_rate", v)} testid={`edit-asm-rate-${a.id}`} type="number" /></div>
            <div><Label>Existing monthly payment ($)</Label><Txt value={f.assumption_monthly_payment} onChange={(v) => up("assumption_monthly_payment", v)} testid={`edit-asm-monthly-${a.id}`} type="number" /></div>
            <div><Label>Remaining term (months)</Label><Txt value={f.assumption_remaining_term} onChange={(v) => up("assumption_remaining_term", v)} testid={`edit-asm-term-${a.id}`} type="number" /></div>
            <div><Label>Next payment due date</Label><Txt value={f.assumption_next_due_date} onChange={(v) => up("assumption_next_due_date", v)} testid={`edit-asm-nextdue-${a.id}`} type="date" /></div>
            <div><Label>Cash-for-keys / relocation ($)</Label><Txt value={f.relocation_incentive} onChange={(v) => up("relocation_incentive", v)} testid={`edit-asm-relocation-${a.id}`} type="number" /></div>
            <div><Label>Vacate date</Label><Txt value={f.vacate_date} onChange={(v) => up("vacate_date", v)} testid={`edit-asm-vacate-${a.id}`} type="date" /></div>
            <div>
              <Label>Deed type</Label>
              <select value={f.deed_type} onChange={(e) => up("deed_type", e.target.value)} data-testid={`edit-asm-deed-${a.id}`}
                className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
                <option value="warranty">Warranty Deed</option>
                <option value="quitclaim">Quitclaim Deed</option>
              </select>
            </div>
          </div>
        </div>
      )}
      <Label>Property / collateral description</Label>
      <Txt value={f.property_or_collateral} onChange={(v) => up("property_or_collateral", v)} testid={`edit-collateral-${a.id}`} />
      {f.debt_type === "mortgage" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Property state</Label>
            <select value={f.property_state} onChange={(e) => { up("property_state", e.target.value); up("note_structure", recommendedNoteStructure(e.target.value)); }} data-testid={`edit-property-state-${a.id}`}
              className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3">
              <option value="">Select state…</option>
              {US_STATES.map(([ab, nm]) => <option key={ab} value={ab}>{nm}</option>)}
            </select>
          </div>
          <div><Label>Property county</Label><Txt value={f.property_county} onChange={(v) => up("property_county", v)} testid={`edit-property-county-${a.id}`} /></div>
        </div>
      )}
      {f.debt_type === "mortgage" && (
        <>
          <Label>Tax Parcel / APN</Label>
          <Txt value={f.tax_parcel_id} onChange={(v) => up("tax_parcel_id", v)} testid={`edit-parcel-${a.id}`} />
          <Label>Legal description (Exhibit A)</Label>
          <textarea value={f.legal_description} onChange={(e) => up("legal_description", e.target.value)} data-testid={`edit-legal-${a.id}`} rows={2}
            className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3" />
        </>
      )}
      <Label>Current lender</Label>
      <Txt value={f.current_lender} onChange={(v) => up("current_lender", v)} testid={`edit-lender-${a.id}`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Lender address</Label><Txt value={f.current_lender_address} onChange={(v) => up("current_lender_address", v)} testid={`edit-lender-address-${a.id}`} /></div>
        <div><Label>Account / loan no.</Label><Txt value={f.account_number} onChange={(v) => up("account_number", v)} testid={`edit-account-${a.id}`} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`edit-counterparty-bank-${a.id}`}>
        <div><Label>Counterparty bank</Label><Txt value={f.counterparty_bank_name} onChange={(v) => up("counterparty_bank_name", v)} testid={`edit-cp-bank-name-${a.id}`} /></div>
        <div><Label>Counterparty ABA</Label><Txt value={f.counterparty_bank_aba} onChange={(v) => up("counterparty_bank_aba", v)} testid={`edit-cp-bank-aba-${a.id}`} /></div>
        <div><Label>Counterparty acct no.</Label><Txt value={f.counterparty_bank_account} onChange={(v) => up("counterparty_bank_account", v)} testid={`edit-cp-bank-account-${a.id}`} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Current balance</Label><Txt value={f.current_balance} onChange={(v) => up("current_balance", v)} testid={`edit-balance-${a.id}`} type="number" /></div>
        <div><Label>Term (months)</Label><Txt value={f.term_months} onChange={(v) => up("term_months", v)} testid={`edit-term-${a.id}`} type="number" /></div>
        <div><Label>Current rate %</Label><Txt value={f.current_rate} onChange={(v) => up("current_rate", v)} testid={`edit-rate-${a.id}`} type="number" /></div>
        <div><Label>Current payment</Label><Txt value={f.current_payment} onChange={(v) => up("current_payment", v)} testid={`edit-curpay-${a.id}`} type="number" /></div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={save} disabled={busy || !f.current_lender || !f.current_balance || !f.borrower_full_name} data-testid={`edit-save-${a.id}`}
          className="inline-flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-40 text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} Save Changes
        </button>
        <button onClick={onCancel} data-testid={`edit-cancel-${a.id}`}
          className="inline-flex items-center gap-1.5 border border-[#2A2A22] hover:border-[#3A3A3A] text-[#9A9A90] px-4 py-2 text-[11px] uppercase tracking-[0.14em] rounded-sm"><X size={13} /> Cancel</button>
      </div>
      {msg && <div className="text-[11px] text-[#FF8585] mt-2" data-testid={`edit-error-${a.id}`}>✗ {msg}</div>}
    </div>
  );
};

const Label = ({ children }) => <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1.5 mt-2">{children}</div>;
const Row = ({ k, v, hi, good }) => <div className="flex justify-between"><span className="text-[#9A9A90]">{k}</span><span className={`font-mono ${good ? "text-[#3DBB8E]" : hi ? "text-[#B8860B] font-bold" : "text-[#ECECE4]"}`}>{v}</span></div>;
const NumInput = ({ value, onChange, testid }) => <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] font-mono" />;
const Txt = ({ value, onChange, testid, type = "text", placeholder }) => <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3" />;
